import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
// import { useAuth } from '../contexts/AuthContext'; // Available if needed
import {
  Box,
  Flex,
  Text,
  Card,
  TextField,
  Button,
  Heading,
  Callout,
  Table,
  Badge,
  TextArea,
  IconButton,
  Separator,
  Dialog
} from '@radix-ui/themes';
import SectionHeading from './common/SectionHeading';
import { TimedProgress, ProgressStep } from './TimedProgress';
import {
  UploadIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  CheckIcon,
  Pencil1Icon,
  CheckCircledIcon,
  CrossCircledIcon,
  Cross2Icon,
  ArrowLeftIcon,
  ArrowRightIcon
} from '@radix-ui/react-icons';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { patientApi, organizationApi, apiRequest, surveyTemplatesApi } from '../services/api';
import { useOrganization } from '../contexts/OrganizationContext';
import { ProblemApprovalItem, MedicationApprovalItem, CarePlanResponse } from '../types/patient';
import ApprovalWorkflow from './ApprovalWorkflow';
import CarePlanDisplay from './CarePlanDisplay';
import CarePlanApproval from './CarePlanApproval';
import SurveyResultsDisplay from './SurveyResultsDisplay';
import VitalSignsInput, { feetInchesToCentimeters, poundsToKilograms, centimetersToFeetInches, kilogramsToPounds, isValidSystolic, isValidDiastolic } from './VitalSignsInput';
import { maskDateInput } from '../utils/dateInputMask';
import { useCarePlanReviewMonths } from '../hooks/useCarePlanReviewMonths';
import { isValidDate } from '../utils/validateDate';
import { getSuggestedICD10Codes } from '../constants/icd10Codes';
import {
  CarePlanRefreshSource,
  REGENERATE_PATIENT_ID_STORAGE_KEY,
  REGENERATE_SOURCE_STORAGE_KEY,
  getInitialStepForRefreshSource,
  getUploadStepContinueLabel,
  getUploadStepPrompt,
  parseCarePlanRefreshSource,
} from '../utils/carePlanRefreshSource';
import { buildRefreshReviewList, isUndecidedNewItem } from '../utils/carePlanRefreshDiff';
import { resolveVoiceOfPatientOrgId } from '../utils/carePlanVoiceOfPatientOrg';
import { collectClinicalDiseasesAcrossPlans } from '../utils/carePlanDiseaseSelection';

// Metadata fields injected by SQL query or frontend logic - exclude from clinical comparison
const METADATA_FIELDS = ['uploaded_ts', 'uploaded_by', 'attributed_ts', 'isNew', 'isModified', 'originalValue', 'approved', 'icd10_needs_review', 'priorState'];

// Per-document LLM parse estimate (gpt-5-mini reasoning is slow); scaled by file count.
const DOC_PARSE_MS_PER_FILE = 45000;
const DOC_PARSE_PROGRESS_STEPS: ProgressStep[] = [
  { label: 'Uploading document…', weight: 1 },
  { label: 'Extracting problems…', weight: 4 },
  { label: 'Extracting medications…', weight: 4 },
  { label: 'Finalizing…', weight: 1 },
];

// PHA-1044: `refreshFromEhr` is asynchronous — it returns 202 and the sync lands later.
// The refresh flow cannot continue until the new candidate batch exists, so it polls for a
// batch stamped later than the one that was there before the trigger. 3s x 30 = 90s, well
// past the ~8s a structured FHIR pull normally takes, and the timeout is not an error: the
// sync keeps running and surfaces in the Smart Hub either way.
const EHR_REFRESH_POLL_INTERVAL_MS = 3000;
const EHR_REFRESH_POLL_ATTEMPTS = 30;

// EHR import has no LLM (structured FHIR), so it is usually quick.
const EHR_IMPORT_PROGRESS_STEPS: ProgressStep[] = [
  { label: 'Connecting to EHR…', weight: 1 },
  { label: 'Fetching problems…', weight: 2 },
  { label: 'Fetching medications…', weight: 2 },
  { label: 'Importing records…', weight: 1 },
];

/**
 * PHA-1046: the most recent candidate batch, split into the two things the refresh flow
 * needs from it.
 *
 * `extracted_data` is only a record of clinician DECISIONS when `reviewed` is true. On an
 * unreviewed batch `approved=false` is just the EHR import's default (PHA-725 writes it;
 * PHA-1029 is the regression from reading it as a rejection), so an unreviewed batch
 * contributes no prior state at all.
 */
export const readPriorCandidateDecisions = (candidatesResponse: any) => ({
  problems:
    candidatesResponse?.problem_list?.reviewed === true
      ? candidatesResponse.problem_list.extracted_data || []
      : [],
  medications:
    candidatesResponse?.medication_list?.reviewed === true
      ? candidatesResponse.medication_list.extracted_data || []
      : [],
});

/** Newest `attributed_ts` across both document types, as a comparable number. */
export const getCandidateBatchStamp = (candidatesResponse: any): number => {
  const stamps = ['problem_list', 'medication_list']
    .map((k) => candidatesResponse?.[k]?.attributed_ts)
    .filter(Boolean)
    .map((ts: string) => new Date(ts).getTime())
    .filter((n: number) => Number.isFinite(n));
  return stamps.length > 0 ? Math.max(...stamps) : 0;
};

/** A timestamp field as a comparable number; NaN for anything that is not a parseable stamp. */
const toStamp = (value: any): number =>
  typeof value === 'string' && value.trim() ? new Date(value).getTime() : NaN;

/**
 * PHA-1067 follow-up: has this candidate batch already been reviewed into staging?
 *
 * The regeneration's Review step no longer writes to `patient_item_candidates` — it stages the
 * decisions instead (that is the whole PHA-1067 fix). One thing the old write did as a side
 * effect was flip the batch's `reviewed` flag, and nothing does that any more. For
 * refreshSource='ehr' the batch in question is the one `refreshFromEhr` inserted at the START of
 * the regeneration, `approved=false, reviewed=false`. So re-entering through "Complete Care Plan
 * Regeneration" read it as brand-new EHR data, restarted the wizard at Review with the raw
 * imported list, and never looked at the staged review at all — the clinician's rejections and
 * edits were simply gone (reported on 13247/AJC Health, dev).
 *
 * A staged review IS the review of the batch it was built from, so it stands in for that flag.
 * Scoped by timestamp rather than mere existence: a nightly EHR import landing between "Save as
 * Draft" and approval produces a NEWER batch that the staged review has not seen, and hiding it
 * would let the approval's promotion (which replaces the whole candidate batch) delete freshly
 * imported items — the same silent-loss family as this ticket. Newer batch ⇒ not covered ⇒ the
 * clinician is sent back through Review, which loses the staged markers but no data.
 */
export const stagedReviewCoversCandidateBatch = (
  stagedItems: any[],
  candidateBatchAttributedTs: any
): boolean => {
  if (!Array.isArray(stagedItems) || stagedItems.length === 0) return false;
  const batchStamp = toStamp(candidateBatchAttributedTs);
  // No batch stamp to compare against: the candidates carry no attribution, so there is no
  // "newer import" to protect. The staged review is the only clinician decision on record.
  if (!Number.isFinite(batchStamp)) return true;
  const stagedStamps = stagedItems
    .map((item) => toStamp(item?.attributed_ts ?? item?.uploaded_ts))
    .filter((n: number) => Number.isFinite(n));
  if (stagedStamps.length === 0) return false;
  return Math.max(...stagedStamps) >= batchStamp;
};

const normalizeForComparison = (item: any) => {
  const copy = { ...item };
  METADATA_FIELDS.forEach(f => delete copy[f]);
  return copy;
};

/**
 * PHA-1029: decide the pre-checked state of the review screen for one candidate batch.
 *
 * The EHR import writes `approved=false, reviewed=false` (PHA-725 — EHR data must not reach
 * the live problem/medication lists without a clinician passing it through review) and
 * `item_candidates_most_recent` stamps that flag onto every item it returns. `?? true` only
 * fills in a MISSING value, so an explicit `false` sailed through and painted every freshly
 * imported row red — the clinician had to re-approve a list nobody had rejected.
 *
 * An unreviewed batch carries no clinician decision, so its `approved=false` carries no
 * information: default the whole batch accepted. A reviewed batch DOES carry decisions
 * (approve posts the full list, and a rejected row is stored `approved=false, reviewed=true`),
 * so preserve it verbatim — otherwise a re-import that returned nothing new hands back the
 * previous batch and resurrects everything the clinician rejected.
 *
 * Nothing here writes to the database: rows stay unapproved until Approve is pressed.
 */
export const applyCandidateApprovalDefaults = (items: any[], reviewed: boolean) =>
  items.map((item: any) => ({ ...item, approved: reviewed ? item.approved ?? true : true }));

/**
 * PHA-1064: where does re-entering an existing patient's wizard land them?
 *
 * This used to be inline, and its first branch was `if (hasCarePlans) setActiveStep(4)` — the
 * "Complete" step. That step renders the "Generating Survey Questions" spinner whenever
 * `surveyResultsGenerated` is false, and no re-entry path ever generates surveys, so an
 * approved-plan patient was parked on a spinner that never resolved and offered no way out.
 *
 * Extracted as a pure function so the rule is testable and so the return type cannot express
 * "the completion step" at all — step 4 is reachable only by actually finishing an approval.
 *
 * - `chart`: onboarding is genuinely done — an approved plan and nothing in flight to resume.
 * - `care-plan`: there is something to work on — a manual draft being regenerated, or approved
 *   problems/medications waiting for a plan.
 * - `upload`: nothing on the chart yet, so the wizard needs documents first.
 */
export type ResumeDestination = 'chart' | 'care-plan' | 'upload';

export const resolveResumeDestination = ({
  hasCarePlans,
  hasAnyItems,
  manualDraftInFlight,
}: {
  hasCarePlans: boolean;
  hasAnyItems: boolean;
  manualDraftInFlight: boolean;
}): ResumeDestination => {
  // A patient mid-regeneration legitimately has an approved plan — it is what the draft is being
  // regenerated FROM — so an approved plan only means "finished" when nothing is in flight.
  if (hasCarePlans && !manualDraftInFlight) return 'chart';
  if (hasAnyItems) return 'care-plan';
  return 'upload';
};

/**
 * Is a resumed session a care-plan REFRESH, or a first-time onboarding being continued?
 *
 * PHA-1073. Both enter through `continuingOnboardingPatientId`, but they are not the same
 * thing, and `refreshSource` is what tells CarePlanApproval which it is. In refresh mode it
 * replaces the first-time program-consent capture with the PHA-726/897 "care plan reviewed
 * with patient" acknowledgment — whose copy asserts "the patient's original program consent
 * is unchanged" — and it stages the item review rather than approving it.
 *
 * PHA-1066 keyed that on `manualDraftInFlight` alone, which reads a saved draft as proof of
 * enrolment. It is not: a clinician who generates a plan and saves it as a draft mid-onboarding
 * has a draft and no consent. Such a patient reached Approve with the acknowledgment checkbox
 * and no way to record consent at all.
 *
 * An APPROVED plan is the only evidence that onboarding actually completed, so a refresh needs
 * both: something in flight to resume, AND a plan to be refreshing FROM.
 */
export const shouldResumeAsRefresh = ({
  manualDraftInFlight,
  hasApprovedCarePlan,
}: {
  manualDraftInFlight: boolean;
  hasApprovedCarePlan: boolean;
}): boolean => manualDraftInFlight && hasApprovedCarePlan;

export const NO_EXTRACTION_ERROR = 'No items extracted. Please add / upload relevant information.';

/**
 * PHA-1079: which sections get the generic "nothing came back" message when a whole processing
 * run extracts zero items.
 *
 * This used to stamp the message onto BOTH sections unconditionally, which produced two wrong
 * outcomes:
 *
 * 1. A clinician who uploaded only a problem list — legal since PHA-1030 made medications
 *    optional — got "Failed to extract medications" under an empty optional section, and no way
 *    to clear it. The medication error only clears by removing a medication file or typing
 *    medication text, and someone onboarding a patient with no medications on file has neither
 *    to give. The red box was unreachable for the rest of the attempt.
 * 2. It said "add / upload relevant information" even when the section had failed for a reason
 *    that had nothing to do with the input. The run behind the report failed with Azure
 *    "401 - Access denied due to invalid subscription key"; that message reached the per-file
 *    progress card and stopped there, so the field underneath asked for more information and the
 *    clinician went off to re-enter a list that was never the problem.
 *
 * So: only sections the clinician actually filled in, and never on top of a message that already
 * says what really failed. `existing` is what makes the second half work — the caller now feeds
 * per-file failures in (see `processDocumentsWithProgress`), which previously went only to the
 * progress card.
 */
export const resolveNoExtractionErrors = ({
  hasProblemListInput,
  hasMedicationListInput,
  existing,
}: {
  hasProblemListInput: boolean;
  hasMedicationListInput: boolean;
  existing: { problemList?: string; medicationList?: string };
}): { problemList?: string; medicationList?: string } => {
  const next = {
    problemList:
      existing.problemList ?? (hasProblemListInput ? NO_EXTRACTION_ERROR : undefined),
    medicationList:
      existing.medicationList ?? (hasMedicationListInput ? NO_EXTRACTION_ERROR : undefined),
  };
  // Fail closed. Callers gate this branch behind "something was submitted", so neither-section
  // should be unreachable — but a run that extracted nothing and reports nothing would fall
  // through to the Review step with an empty list, which is worse than one spurious box.
  if (!next.problemList && !next.medicationList) {
    next.problemList = NO_EXTRACTION_ERROR;
  }
  return next;
};

// Utility function to compare items and mark diffs
export const compareItems = (approvedItems: any[], candidateItems: any[], keyField: string) => {
  const approvedMap = new Map(approvedItems.map(item => [item[keyField]?.toLowerCase(), item]));
  const result: any[] = [];

  // Process candidate items
  candidateItems.forEach(candidate => {
    const key = candidate[keyField]?.toLowerCase();
    const approved = approvedMap.get(key);

    if (!approved) {
      // New item not in approved list
      // PHA-1029: a candidate absent from the approved list is new — keep the default the
      // caller already stamped on (true for an unreviewed batch), rather than hardcoding it.
      result.push({ ...candidate, isNew: true, approved: candidate.approved ?? true });
    } else {
      // Compare only clinical fields, ignoring metadata
      const isModified =
        JSON.stringify(normalizeForComparison(candidate)) !==
        JSON.stringify(normalizeForComparison(approved));
      result.push({
        ...candidate,
        isModified,
        originalValue: isModified ? approved : undefined,
        // PHA-725: approval is NOT derived from isModified. isModified only drives the yellow
        // highlight + "Original:" display — auto-deselecting on any residual candidate/approved
        // JSON mismatch (or a genuine edit) blocked the Approve button.
        // PHA-1029: the default itself now comes from applyCandidateApprovalDefaults upstream —
        // true for an unreviewed batch, the clinician's stored decision for a reviewed one.
        approved: candidate.approved ?? true,
      });
    }
  });

  return result;
};

/**
 * PHA-1067 follow-up (Zack): a saved draft's accept/reject/edit decisions must survive a daily
 * EHR import.
 *
 * A nightly EHR import that lands between "Save as Draft" and approval produces a NEWER candidate
 * batch than the staged review, so `stagedReviewCoversCandidateBatch` (correctly) reports it as
 * not covered and the wizard re-opens Review — the clinician does need to see genuinely new EHR
 * items, and promotion replaces the whole candidate batch, so anything missing from the approved
 * set is dropped. But opening Review on the raw import discarded every decision the clinician had
 * already made: the medications they rejected came back, their edits were gone. That is the
 * report.
 *
 * This lays the staged decisions back over the freshly imported list instead of choosing between
 * them:
 *   - a matched item keeps the clinician's decision and their edits (their reject stays a reject);
 *   - an item only in the import is surfaced as new, for the clinician to review;
 *   - a decision on an item the import no longer lists is kept, so a rejection — or an item the
 *     clinician added during the draft — is not silently lost either.
 *
 * Every returned row is stamped with ONE `attributed_ts`. The live problem_list / medication_list
 * views show only the single newest approved batch (`DISTINCT ON ... ORDER BY attributed_ts
 * DESC`), and `update_patient_data_approval` keeps per-item stamps whenever a promoted batch
 * carries more than one — so a union of the clinician's older staged items and the fresh import
 * would promote as two batches and half of it would disappear from the live list. Collapsing to
 * the newest stamp on record (the import's when there is one, else the staged one) keeps the whole
 * reconciled list in a single batch through the approve → promote path.
 */
export const overlayStagedDecisions = (
  comparedItems: any[],
  stagedItems: any[],
  approvedItems: any[],
  keyField: string
): any[] => {
  if (!Array.isArray(stagedItems) || stagedItems.length === 0) return comparedItems;

  const batchStamp =
    comparedItems.find((i) => i?.attributed_ts)?.attributed_ts ??
    stagedItems.find((i) => i?.attributed_ts)?.attributed_ts ??
    null;
  const stamp = (item: any) => (batchStamp ? { ...item, attributed_ts: batchStamp } : item);

  const approvedMap = new Map(approvedItems.map((item) => [item[keyField]?.toLowerCase(), item]));
  const stagedMap = new Map(stagedItems.map((item) => [item[keyField]?.toLowerCase(), item]));
  const usedStaged = new Set<string>();

  const fromStaged = (staged: any) => {
    const approved = approvedMap.get(staged[keyField]?.toLowerCase());
    const isModified = approved
      ? JSON.stringify(normalizeForComparison(staged)) !==
        JSON.stringify(normalizeForComparison(approved))
      : false;
    // The clinician's own decision wins over the raw import: keep their edits, keep their
    // accept/reject. It is not "new" to them, whatever the fresh candidate batch says.
    return stamp({
      ...staged,
      isNew: false,
      isModified,
      originalValue: isModified ? approved : undefined,
      approved: staged.approved ?? true,
    });
  };

  const merged = comparedItems.map((item) => {
    const key = item[keyField]?.toLowerCase();
    const staged = stagedMap.get(key);
    if (!staged) return stamp(item); // genuinely new imported item — compareItems already flagged it
    usedStaged.add(key);
    return fromStaged(staged);
  });

  // Decisions on items the fresh import no longer lists — a rejection, or an item the clinician
  // added during the draft — must not vanish just because the EHR batch rolled over.
  stagedItems.forEach((staged) => {
    const key = staged[keyField]?.toLowerCase();
    if (usedStaged.has(key)) return;
    merged.push(fromStaged(staged));
  });

  return merged;
};

const deduplicateProblems = (problems: any[]): any[] => {
  const groups = new Map<string, any[]>();

  for (const p of problems) {
    const key = [
      (p.problem || '').toLowerCase().trim(),
      (p.icd10_code || '').toLowerCase().trim(),
    ].join('||');

    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(p);
  }

  const statusPriority = (s: string) => {
    switch ((s || 'active').toLowerCase()) {
      case 'active': return 0;
      case 'inactive': return 1;
      case 'resolved': return 2;
      default: return 3;
    }
  };

  return Array.from(groups.values()).map(group => {
    if (group.length === 1) return group[0];
    // Prefer "active" status, then most recent onset_date
    return group.reduce((best, curr) => {
      const sp = statusPriority(curr.clinical_status) - statusPriority(best.clinical_status);
      if (sp !== 0) return sp < 0 ? curr : best;
      const bestDate = best.onset_date || '';
      const currDate = curr.onset_date || '';
      return currDate > bestDate ? curr : best;
    });
  });
};

const enrichProblemsWithICD10 = (problems: any[]): any[] => {
  return problems.map(problem => {
    if (problem.icd10_code) return problem; // already has a code
    const suggestions = getSuggestedICD10Codes(problem.problem || '');
    if (suggestions.length > 0) {
      // If the LLM returned a description but no code, try to match the description
      let bestMatch = suggestions[0];
      if (problem.icd10_description && suggestions.length > 1) {
        const descLower = problem.icd10_description.toLowerCase();
        const descMatch = suggestions.find((s: { description: string }) =>
          s.description.toLowerCase().includes(descLower) ||
          descLower.includes(s.description.toLowerCase())
        );
        if (descMatch) bestMatch = descMatch;
      }
      return {
        ...problem,
        icd10_code: bestMatch.code,
        icd10_description: bestMatch.description,
        icd10_needs_review: true, // auto-populated, needs clinician confirmation
      };
    }
    return problem; // no match found, leave empty
  });
};

interface PatientFormData {
  patient_id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  phone_number: string;
  gender: string;
  preferred_contact_method: 'sms' | 'voice';
  language: string;
}

interface UploadedFile {
  file: File;
  name: string;
  size: number;
}

interface Question {
  id: string;
  question: string;
  format: string;
  cadence: string;
  type: string;
}

interface VoiceScript {
  id: string;
  title: string;
  content: string;
  type: string;
}

interface ParsedProblem {
  problem: string;
  onset_date?: string;
  clinical_status?: string;
  approved?: boolean;
  isNew?: boolean;
  isModified?: boolean;
  originalValue?: any;
  uploaded_ts?: string;
  uploaded_by?: string;
  icd10_code?: string;
  icd10_description?: string;
  icd10_needs_review?: boolean;
  snomed_code?: string;
  snomed_description?: string;
}

interface ParsedMedication {
  medication: string;
  dosage?: string;
  start_date?: string;
  end_date?: string;
  approved?: boolean;
  isNew?: boolean;
  isModified?: boolean;
  originalValue?: any;
  uploaded_ts?: string;
  uploaded_by?: string;
}

interface ExtractedData {
  problems: ParsedProblem[];
  medications: ParsedMedication[];
}

interface VitalSigns {
  blood_pressure_systolic: string;
  blood_pressure_diastolic: string;
  height_feet: string;
  height_inches: string;
  weight_pounds: string;
  BMI: string;
  input_mode: 'height_weight' | 'bmi_only';
}

// PHA-773: the host Dashboard uses this to surface the in-progress patient's
// name/MRN on the Onboarding tab (otherwise the tab is just a generic "Onboarding").
interface OnboardingWorkflowProps {
  onPatientIdentityChange?: (identity: { firstName: string; lastName: string; mrn: string }) => void;
  /** PHA-895: forwarded to CarePlanApproval so the Dashboard can prompt Save/Discard before the
   *  user leaves the onboarding tab with unsaved edits. */
  onUnsavedChangesChange?: (hasUnsaved: boolean) => void;
  registerDraftControls?: (
    controls: { save: () => Promise<void>; discard: () => Promise<void> } | null,
  ) => void;
  /** PHA-967: leave the wizard and open this patient's chart on the Care Plans tab. Called after
   *  the care plan is saved as a draft, so the clinician reviews what they just saved instead of
   *  navigating back through the patient list. NOT used by Approve — that path ends on the
   *  "Complete" step (activeStep 4), whose Done button resets to the patient list. */
  onExitToPatientChart?: (patient: {
    patientId: string;
    firstName?: string;
    lastName?: string;
    mrn?: string | null;
  }) => void;
}

const OnboardingWorkflow: React.FC<OnboardingWorkflowProps> = ({
  onPatientIdentityChange,
  onUnsavedChangesChange,
  registerDraftControls,
  onExitToPatientChart,
}) => {
  // Auth context available if needed: const auth = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  // PHA-959: the per-org feature flags below must follow the clinic selector. The
  // Onboarding tab stays mounted across an org switch, so a mount-only check keeps
  // answering for the previously selected clinic.
  const { orgId: selectedOrgId, loading: orgLoading } = useOrganization();
  // "ALL" (internal all-clinics view) and "" (no selection yet) are not real orgs —
  // send nothing and let the server fall back to the session organization.
  const flagOrgId = selectedOrgId && selectedOrgId !== 'ALL' ? String(selectedOrgId) : undefined;
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExistingPatient, setIsExistingPatient] = useState(false);

  // Field-level error tracking for validation (stores specific error messages)
  const [fieldErrors, setFieldErrors] = useState<{
    patient_id?: string;
    first_name?: string;
    last_name?: string;
    date_of_birth?: string;
    phone_number?: string;
    gender?: string;
  }>({});

  // State for duplicate patient dialog
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateDialogMessage, setDuplicateDialogMessage] = useState('');
  const [pendingPatientData, setPendingPatientData] = useState<PatientFormData | null>(null);
  // Which flow opened the shared dialog. Non-null means "Import from EHR" hit a duplicate
  // MRN, so confirming re-imports from the EHR rather than saving the typed form fields.
  const [pendingEhrImportMrn, setPendingEhrImportMrn] = useState<string | null>(null);

  // Step 1: Patient Information
  const [patientData, setPatientData] = useState<PatientFormData>({
    patient_id: '',
    first_name: '',
    last_name: '',
    date_of_birth: '',
    phone_number: '',
    gender: '',
    preferred_contact_method: 'sms',
    language: 'en-US',
  });

  // PHA-773: report the in-progress patient identity up to the Dashboard so the
  // Onboarding tab can show the patient's name/MRN instead of a generic label.
  // Fires whenever name/MRN change (typed manually, EHR-imported, or resumed).
  useEffect(() => {
    onPatientIdentityChange?.({
      firstName: patientData.first_name?.trim() || '',
      lastName: patientData.last_name?.trim() || '',
      mrn: patientData.patient_id?.trim() || '',
    });
  }, [patientData.first_name, patientData.last_name, patientData.patient_id, onPatientIdentityChange]);

  // Voice AI org setting
  const [voiceEnabled, setVoiceEnabled] = useState(false);

  // PHA-865: "PCMH Voice of patient intake" org flag — gates the Full Problem/Medication
  // List sections in the draft care-plan PDF.
  const [voiceOfPatientEnabled, setVoiceOfPatientEnabled] = useState(false);
  // PHA-1048: the loaded patient's own organization. The org-flag lookups below used to key
  // off the org SWITCHER, which is undefined on "All clinics" — see
  // resolveVoiceOfPatientOrgId for why that silently dropped the Voice-of-the-Patient
  // section from a regenerated care plan.
  const [patientOrgId, setPatientOrgId] = useState<string | null>(null);

  // PHA-1117: resolve patientOrgId for a patient the wizard just CREATED. loadExistingPatientData
  // below sets it for a continuation or a care-plan refresh, but a brand-new patient — the manual
  // Step-1 create and the "Import from EHR" create — never went through that path, so patientOrgId
  // stayed null for the whole wizard. Everything keyed off it then fell back to its no-org default,
  // which is how a clinic configured for a 3-month review cycle still approved a plan on the
  // 6-month default (and how the on-screen "Default is N months" line still read 6).
  //
  // The patient record is the authority, not `selectedOrgId`: the create call omits the org when
  // the switcher sits on "All clinics" and the server files the patient under the session's clinic,
  // so the switcher is not always what the patient ended up in. Reads the same
  // `organization_id || organization` expression loadExistingPatientData uses.
  //
  // Non-fatal: a failure here leaves patientOrgId null, i.e. exactly today's behaviour.
  const syncPatientOrgIdFromRecord = useCallback(async (mrn: string) => {
    if (!mrn?.trim()) return;
    try {
      const patient: any = await patientApi.get(mrn.trim());
      const resolved = String(patient?.organization_id || patient?.organization || '').trim();
      if (resolved) setPatientOrgId(resolved);
    } catch (err) {
      console.error("PHA-1117: failed to resolve the new patient's clinic:", err);
    }
  }, []);

  // PHA-895 follow-up: org "no target dates" flag — must be forwarded to CarePlanApproval so the
  // onboarding on-screen plan AND the draft PDF match the patient chart (which hides Due Date /
  // Achieved columns for these orgs). Without it the draft PDF wrongly shows those columns.
  const [noTargetDatesEnabled, setNoTargetDatesEnabled] = useState(false);

  // PHA-1080: the clinic's care plan review cycle, forwarded to CarePlanApproval for the
  // same reason noTargetDatesEnabled above is — the onboarding wizard's review-date field
  // is what updateCarePlanExpiration writes on approve, so a clinic on a 12-month cycle has
  // to see 12 months here, not the 6-month default. Keyed on patientOrgId, which this
  // component already resolves for the org-flag lookups below.
  const carePlanReviewMonths = useCarePlanReviewMonths(patientOrgId);

  // Supported languages for the organization
  const [supportedLanguages, setSupportedLanguages] = useState<Array<{code: string, name: string, native_name: string}>>([]);

  // Phone type verification warning (shown as toast, stored for reference)
  const [phoneTypeWarning, setPhoneTypeWarning] = useState<string | null>(null); // eslint-disable-line @typescript-eslint/no-unused-vars

  // EHR Import state
  const [hasEhrIntegration, setHasEhrIntegration] = useState(false);
  const [ehrImportLoading, setEhrImportLoading] = useState(false);
  const [ehrImported, setEhrImported] = useState(false);
  // PHA-958: set when the EHR returned a chart with no phone number on file. The import
  // wrote nothing on that pass, so the wizard stays editable and the clinician supplies a
  // number, which the retry sends along. Deliberately NOT `ehrImported` — that flag
  // disables every demographic field and the Import button itself, which would leave the
  // clinician unable to either type the phone or resubmit.
  const [ehrPhoneRequired, setEhrPhoneRequired] = useState(false);
  // PHA-397: shown when duplicate-MRN re-import matches an existing patient
  const [existingPatientMatch, setExistingPatientMatch] = useState<{
    patient_id: string;
    first_name: string;
    last_name: string;
    date_of_birth: string;
    mrn: string;
  } | null>(null);
  const [ehrImportResult, setEhrImportResult] = useState<{
    created: boolean;
    candidates_added: number;
    problems_count: number;
    medications_count: number;
    vitals_count: number;
  } | null>(null);

  // Step 2: Document Upload
  const [problemListFiles, setProblemListFiles] = useState<UploadedFile[]>([]);
  const [medicationListFiles, setMedicationListFiles] = useState<UploadedFile[]>([]);
  const [problemListText, setProblemListText] = useState('');
  const [medicationListText, setMedicationListText] = useState('');

  const [documentUploadErrors, setDocumentUploadErrors] = useState<{
    problemList?: string;
    medicationList?: string;
  }>({});

  const [parseErrors, setParseErrors] = useState<{
    problemList?: string;
    medicationList?: string;
  }>({});

  // State for pasted images
  const [pastedProblemImages, setPastedProblemImages] = useState<UploadedFile[]>([]);
  const [pastedMedicationImages, setPastedMedicationImages] = useState<UploadedFile[]>([]);

  // Vital signs state
  const [vitalSigns, setVitalSigns] = useState<VitalSigns>({
    blood_pressure_systolic: '',
    blood_pressure_diastolic: '',
    height_feet: '',
    height_inches: '',
    weight_pounds: '',
    BMI: '',
    input_mode: 'height_weight'
  });

  // Previously uploaded document names (for continuation flow)
  const [previousDocumentNames, setPreviousDocumentNames] = useState<{
    problem_list?: string[];
    medication_list?: string[];
  }>({});

  // Upload progress modal state
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    files: Array<{
      name: string;
      status: 'pending' | 'uploading' | 'completed' | 'error';
      progress: number;
      error?: string;
    }>;
    overallProgress: number;
    currentFile?: string;
  }>({
    files: [],
    overallProgress: 0
  });

  // Step 3: Review Parsed Information
  const [extractedData, setExtractedData] = useState<ExtractedData>({ problems: [], medications: [] });

  // Track whether documents have been processed (to skip re-processing on back-navigation)
  const [documentsProcessed, setDocumentsProcessed] = useState(false);
  const [processedInputSnapshot, setProcessedInputSnapshot] = useState<string | null>(null);
  const [vitalsUploaded, setVitalsUploaded] = useState(false);
  const [uploadedVitalsSnapshot, setUploadedVitalsSnapshot] = useState<string | null>(null);


  // Step 4: Care Plan
  // PHA-737: patient/org fields the draft PDF needs (logo, program, org name). patientData
  // (PatientFormData) intentionally drops these, so we fetch them once when Step 3 is reached.
  const [patientPdfMeta, setPatientPdfMeta] = useState<{
    apcm_tier?: number | null;
    // PHA-1164: lists RPM among the patient's programs on the PDF.
    has_rpm_device?: boolean | null;
    // PHA-1195: prints the draft in the patient's language.
    language?: string | null;
    logo_path?: string;
    organization_name?: string;
    // PHA-895 follow-up (PHA-737 parity): the persisted problem/medication lists (with onset /
    // start / end dates) as the patient chart reads them, so the draft PDF's Full Problem/Med
    // List matches the chart's byte-for-byte instead of the date-less onboarding extraction copy.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    problems?: any[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    medications?: any[];
    // PHA-899: person-centered (Voice of the Patient) data so the onboarding draft PDF's
    // "Voice of the patient" section matches the patient chart's "Download Care Plan". Without
    // these, the draft always rendered "No goals were defined." even when goals existed (the
    // chart mount forwards them, onboarding did not).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    person_centered_goals?: any[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pcm_barriers?: any[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gas_definitions?: any[];
  }>({});
  const [carePlanData, setCarePlanData] = useState<CarePlanResponse | null>(null);
  const [carePlanLoading, setCarePlanLoading] = useState(false);
  const [carePlanGenerated, setCarePlanGenerated] = useState(false);
  const [carePlanDirtySinceGeneration, setCarePlanDirtySinceGeneration] = useState(false);

  // PHA-895: memoize so the reference stays stable across re-renders. CarePlanApproval rebuilds
  // its editable state whenever `approvedProblems` changes identity (its hydrate effect); passing
  // a fresh inline `.filter()` array on every parent re-render would wipe in-progress edits the
  // moment the unsaved-changes callback re-renders this component.
  const approvedProblemsForCarePlan = useMemo(
    () => extractedData.problems.filter((p) => p.approved) as ProblemApprovalItem[],
    [extractedData.problems],
  );

  // PHA-895 follow-up (PHA-737 parity): the draft PDF's Full Problem/Med List must match the
  // patient chart. The chart reads the PERSISTED lists (onset/start/end dates populated); the
  // onboarding extraction copy has the right items (approved-only, per PHA-865) but no dates.
  // Enrich each approved onboarding item with the dates from the matching persisted row (by
  // ICD-10, else by name) so the printed draft matches the chart without re-introducing
  // clinician-rejected items. Falls back to the plain filtered copy until the Step-3 fetch lands.
  const problemsForPdf = useMemo(() => {
    const approved = extractedData.problems.filter((p) => p.approved !== false);
    const persisted = patientPdfMeta.problems;
    if (!persisted || persisted.length === 0) return approved;
    const byCode = new Map<string, any>();
    const byName = new Map<string, any>();
    for (const pp of persisted) {
      const code = (pp?.icd10_code || '').toLowerCase().trim();
      const name = (pp?.problem || '').toLowerCase().trim();
      if (code && !byCode.has(code)) byCode.set(code, pp);
      if (name && !byName.has(name)) byName.set(name, pp);
    }
    return approved.map((p) => {
      const code = (p.icd10_code || '').toLowerCase().trim();
      const name = (p.problem || '').toLowerCase().trim();
      const match = (code && byCode.get(code)) || (name && byName.get(name));
      if (!match) return p;
      return {
        ...p,
        onset_date: p.onset_date || match.onset_date,
        clinical_status: p.clinical_status || match.clinical_status,
      };
    });
  }, [extractedData.problems, patientPdfMeta.problems]);

  const medicationsForPdf = useMemo(() => {
    const approved = extractedData.medications.filter((m) => m.approved !== false);
    const persisted = patientPdfMeta.medications;
    if (!persisted || persisted.length === 0) return approved;
    const byName = new Map<string, any>();
    for (const pm of persisted) {
      const name = (pm?.medication || '').toLowerCase().trim();
      if (name && !byName.has(name)) byName.set(name, pm);
    }
    return approved.map((m) => {
      const match = byName.get((m.medication || '').toLowerCase().trim());
      if (!match) return m;
      return {
        ...m,
        dosage: m.dosage || match.dosage,
        start_date: (m as any).start_date || match.start_date,
        end_date: (m as any).end_date || match.end_date,
      };
    });
  }, [extractedData.medications, patientPdfMeta.medications]);

  // Step 5: Complete (Survey Results + Done)
  const [surveyResults, setSurveyResults] = useState<any[]>([]);
  const [surveyResultsGenerated, setSurveyResultsGenerated] = useState(false);
  const [patientConsentGiven, setPatientConsentGiven] = useState<boolean>(false);
  const [loadedConsentStatus, setLoadedConsentStatus] = useState<'yes' | 'no' | null>(null);

  // Questions state (keeping for backward compatibility)
  const [questions, setQuestions] = useState<Question[]>([]);
  const [voiceScripts, setVoiceScripts] = useState<VoiceScript[]>([]);
  // PHA-1033: the care-plan REFRESH flow. Null means this is a first-time onboarding, and
  // every refresh-only behaviour below is gated on that — the creation workflow must not
  // change (PHA-1033 is explicit about it).
  const [refreshSource, setRefreshSource] = useState<CarePlanRefreshSource | null>(null);
  // PHA-1046: what the last approved plan looked like, captured on entry so the Review step
  // can mark each item kept / rejected / new. Held in state because the 'upload' source
  // does not have its items until documents are parsed, several steps later.
  const [refreshBaseline, setRefreshBaseline] = useState<{
    approvedProblems: any[];
    approvedMedications: any[];
    priorProblemCandidates: any[];
    priorMedicationCandidates: any[];
    /** PHA-1047: conditions the CURRENT approved plan focuses on, to pre-check the picker. */
    currentPlanDiseases: string[];
    /** PHA-945: false for a draft-only patient — changes what Cancel warns about. */
    hasApprovedPlan: boolean;
  } | null>(null);
  const [ehrRefreshBusy, setEhrRefreshBusy] = useState(false);
  // PHA-1044: the EHR poll can run for 90s. Everything else async in this component
  // completes in seconds, so nothing here has needed an unmount guard before — this does.
  // Without it a clinician who navigates away mid-poll gets setState on an unmounted
  // component, and worse, a late `enterRefreshReview` can land step/extractedData on top of
  // a resetWorkflow and drop them back into a half-built wizard.
  const refreshAbandonedRef = useRef(false);
  const [questionsGenerated, setQuestionsGenerated] = useState(false);

  const steps = [
    'Patient Information',
    'Document Upload',
    'Review patient information',
    'Approve Care Plan',
    'Complete'
  ];

  // Snapshot helpers for detecting input changes (used to skip re-processing on back-navigation)
  const getInputSnapshot = (): string => {
    return JSON.stringify({
      pf: problemListFiles.map(f => f.name + '_' + f.size).sort(),
      mf: medicationListFiles.map(f => f.name + '_' + f.size).sort(),
      pp: pastedProblemImages.map(f => f.name + '_' + f.size).sort(),
      pm: pastedMedicationImages.map(f => f.name + '_' + f.size).sort(),
      pt: problemListText.trim(),
      mt: medicationListText.trim(),
    });
  };

  const getVitalsSnapshot = (): string => JSON.stringify(vitalSigns);

  const getCarePlanDirtyKey = () => `carePlanDirty_${patientData.patient_id}`;
  const persistCarePlanDirty = (dirty: boolean) => {
    if (dirty) {
      localStorage.setItem(getCarePlanDirtyKey(), 'true');
    } else {
      localStorage.removeItem(getCarePlanDirtyKey());
    }
  };

  useEffect(() => () => { refreshAbandonedRef.current = true; }, []);

  // Scroll to top and focus container when step changes
  useEffect(() => {
    window.scrollTo(0, 0);
    containerRef.current?.focus();
  }, [activeStep]);

  // Check for continuation or care-plan regeneration on mount
  useEffect(() => {
    console.log('OnboardingWorkflow mounted, checking for continuation...');
    const regeneratePatientId = sessionStorage.getItem(REGENERATE_PATIENT_ID_STORAGE_KEY);
    if (regeneratePatientId) {
      // PHA-1043: the source picker in PatientDetail stashes which input the clinician
      // chose — 'current', 'documents', 'ehr' or 'upload'. Anything unrecognised falls back
      // to 'current', which imports nothing.
      const regenerateSource = parseCarePlanRefreshSource(
        sessionStorage.getItem(REGENERATE_SOURCE_STORAGE_KEY)
      );
      console.log('Starting care plan regeneration flow for patient:', regeneratePatientId, 'source:', regenerateSource);
      sessionStorage.removeItem(REGENERATE_PATIENT_ID_STORAGE_KEY);
      sessionStorage.removeItem(REGENERATE_SOURCE_STORAGE_KEY);
      setRefreshSource(regenerateSource);
      loadExistingPatientData(regeneratePatientId, {
        regenerateCarePlan: true,
        regenerateSource,
      });
      return;
    }

    const continuePatientId = sessionStorage.getItem('continuingOnboardingPatientId');
    console.log('Found continuation patient ID in sessionStorage:', continuePatientId);

    if (continuePatientId) {
      console.log('Starting continuation flow for patient:', continuePatientId);
      sessionStorage.removeItem('continuingOnboardingPatientId');
      loadExistingPatientData(continuePatientId);
    } else {
      console.log('No continuation patient ID found');
    }
  }, []);

  // PHA-1162: re-read only the person-centered (Voice of the Patient) fields after a goal is
  // saved from the approval step; the rest of the PDF meta is unchanged.
  const refreshVoiceOfPatientForPdf = async () => {
    const patientId = patientData.patient_id;
    if (!patientId) return;
    const patient = await patientApi.get(patientId);
    setPatientPdfMeta(prev => ({
      ...prev,
      person_centered_goals: Array.isArray((patient as any)?.person_centered_goals) ? (patient as any).person_centered_goals : [],
      pcm_barriers: Array.isArray((patient as any)?.pcm_barriers) ? (patient as any).pcm_barriers : [],
      gas_definitions: Array.isArray((patient as any)?.gas_definitions) ? (patient as any).gas_definitions : [],
    }));
  };

  // PHA-737: when the care-plan step (Step 3) is reached, fetch the patient/org fields the draft
  // PDF needs (apcm_tier → program, logo_path, organization_name). This is the single choke point
  // every Step-3 entry passes through (new, existing, continue, regenerate); the patient always
  // exists in the DB by then since care-plan candidates are keyed to patient_id.
  useEffect(() => {
    const patientId = patientData.patient_id;
    if (activeStep !== 3 || !patientId) return;
    let cancelled = false;
    (async () => {
      try {
        const patient = await patientApi.get(patientId);
        if (cancelled) return;
        setPatientPdfMeta({
          apcm_tier: patient?.apcm_tier ?? null,
          has_rpm_device: (patient as any)?.has_rpm_device ?? null,
          language: patient?.language ?? null,
          logo_path: patient?.logo_path,
          organization_name: patient?.organization_name,
          // PHA-895 follow-up (PHA-737 parity): reuse the chart's persisted problem/med lists
          // (they carry onset/start/end dates the onboarding extraction copy lacks).
          problems: Array.isArray((patient as any)?.problems) ? (patient as any).problems : undefined,
          medications: Array.isArray((patient as any)?.medications) ? (patient as any).medications : undefined,
          // PHA-899: forward person-centered goals/barriers/GAS so the draft PDF's "Voice of the
          // patient" section matches the chart. patientApi.get already returns these (aggregated
          // over ALL of the patient's goals — no care-plan-time filter), so a goal added after the
          // care plan was created is included here just as it is in the chart's Download Care Plan.
          person_centered_goals: Array.isArray((patient as any)?.person_centered_goals) ? (patient as any).person_centered_goals : undefined,
          pcm_barriers: Array.isArray((patient as any)?.pcm_barriers) ? (patient as any).pcm_barriers : undefined,
          gas_definitions: Array.isArray((patient as any)?.gas_definitions) ? (patient as any).gas_definitions : undefined,
        });
        // PHA-895 follow-up: resolve the org's no-target-dates flag so the onboarding plan + draft
        // PDF render the Health Goals table the same way the patient chart does.
        // PHA-907: mirror the hardened PatientDetail path. Prefer the value delivered inline with
        // the patient payload (atomic with the data, no separate request, no race); only fall back
        // to a dedicated request — with retries, so a single transient blip does not fail open and
        // leak due dates for a flagged org — when the inline value is absent.
        const inlineNtd = (patient as any)?.no_target_dates_enabled;
        if (typeof inlineNtd === 'boolean') {
          if (!cancelled) setNoTargetDatesEnabled(inlineNtd);
        } else {
          const orgId = (patient as any)?.organization_id || (patient as any)?.organization;
          if (orgId) {
            const resolveNtd = async (attempt = 1): Promise<void> => {
              try {
                const ntd = await patientApi.getOrgNoTargetDatesEnabled(String(orgId));
                if (!cancelled) setNoTargetDatesEnabled(ntd?.no_target_dates_enabled === true);
              } catch {
                if (cancelled) return;
                if (attempt < 3) {
                  setTimeout(() => { void resolveNtd(attempt + 1); }, 400 * attempt);
                  return;
                }
                setNoTargetDatesEnabled(false);
              }
            };
            await resolveNtd();
          }
          // PHA-904: resolve the "Voice of patient intake" flag from the patient's EXPLICIT org
          // id (mirrors PatientDetail). The mount-time check sends an empty org id and relies on
          // the server session's current_organization_id, which isn't reliably set in onboarding
          // — so the draft PDF dropped the Voice-of-Patient, Full Problem List and Full
          // Medication List sections (all gated on this flag) even though the chart's Download
          // Care Plan rendered them.
          try {
            const vop = await patientApi.getOrgVoiceOfPatientEnabled(String(orgId));
            if (!cancelled) setVoiceOfPatientEnabled(vop?.voice_of_patient_enabled === true);
          } catch {
            /* keep the mount-time value as fallback */
          }
          // PHA-904: resolve the "Voice of patient intake" flag from the patient's EXPLICIT org
          // id (mirrors PatientDetail). The mount-time check sends an empty org id and relies on
          // the server session's current_organization_id, which isn't reliably set in onboarding
          // — so the draft PDF dropped the Voice-of-Patient, Full Problem List and Full
          // Medication List sections (all gated on this flag) even though the chart's Download
          // Care Plan rendered them.
          try {
            const vop = await patientApi.getOrgVoiceOfPatientEnabled(String(orgId));
            if (!cancelled) setVoiceOfPatientEnabled(vop?.voice_of_patient_enabled === true);
          } catch {
            /* keep the mount-time value as fallback */
          }
        }
      } catch (err) {
        console.error('PHA-737: failed to load patient PDF metadata for draft print:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // PHA-899: also refetch when the care plan is (re)generated — that is the natural point at
    // which a just-added Voice-of-the-Patient goal is finalized, so the draft PDF picks it up
    // without waiting for a Step-3 re-entry.
  }, [activeStep, patientData.patient_id, carePlanGenerated]);

  // PHA-959: per-org feature flags, resolved against the CURRENTLY selected clinic.
  //
  // These used to run once on mount with an empty org id, letting the server answer
  // for whatever organization the BFF session happened to hold. That broke two ways:
  //   1. Org switch — the Onboarding tab stays mounted, so switching from a clinic
  //      without EHR integration to MMC left "Import from EHR" hidden even though the
  //      header read MMC.
  //   2. Mount race — on a fresh page load the BFF caches the backend's auto-selected
  //      (alphabetically first) org, and this check could fire before OrganizationContext
  //      restored the user's real clinic.
  // Keying on orgId (and waiting for the context to settle) fixes both.
  useEffect(() => {
    if (orgLoading) return;
    let cancelled = false;

    const checkOrgFlags = async () => {
      try {
        const result = await patientApi.checkOrgEhrIntegration(flagOrgId);
        if (!cancelled) setHasEhrIntegration(result.has_ehr_integration === true);
      } catch (err) {
        console.error('Failed to check EHR integration:', err);
        if (!cancelled) setHasEhrIntegration(false);
      }

      try {
        const result = await patientApi.checkOrgVoiceEnabled(flagOrgId);
        if (!cancelled) setVoiceEnabled(result.voice_enabled === true);
      } catch (err) {
        console.error('Failed to check voice enabled:', err);
        if (!cancelled) setVoiceEnabled(false);
      }

      // PHA-865/PHA-904: "PCMH Voice of patient intake" gates the VoP, Full Problem List
      // and Full Medication List sections of the draft PDF.
      // PHA-1048: resolve from the PATIENT's org when one is loaded, matching what
      // PatientDetail has always done. Keying off the switcher meant the same patient's plan
      // showed VoP on the chart and lost it in the wizard and the draft PDF.
      const vopOrgId = resolveVoiceOfPatientOrgId(patientOrgId, flagOrgId);
      if (!vopOrgId) {
        // No org to ask about — fail closed rather than letting the BFF guess from the
        // session, which is what made this unpredictable in the first place.
        if (!cancelled) setVoiceOfPatientEnabled(false);
      } else {
        try {
          const result = await patientApi.getOrgVoiceOfPatientEnabled(vopOrgId);
          if (!cancelled) setVoiceOfPatientEnabled(result.voice_of_patient_enabled === true);
        } catch (err) {
          console.error('Failed to check voice of patient enabled:', err);
          if (!cancelled) setVoiceOfPatientEnabled(false);
        }
      }
    };

    checkOrgFlags();
    return () => {
      cancelled = true;
    };
  }, [flagOrgId, orgLoading, patientOrgId]);

  // Fetch org supported languages
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const data = await organizationApi.getSupportedLanguages();
        if (Array.isArray(data)) {
          setSupportedLanguages(data);
        }
      } catch (err) {
        console.error('Failed to fetch supported languages:', err);
        setSupportedLanguages([{ code: 'en-US', name: 'English', native_name: 'English' }]);
      }
    };
    fetchLanguages();
  }, []);

  // Load existing patient data for continuation or care-plan regeneration
  const loadExistingPatientData = async (
    patientId: string,
    options?: { regenerateCarePlan?: boolean; regenerateSource?: CarePlanRefreshSource }
  ) => {
    console.log('loadExistingPatientData called with patientId:', patientId, options);
    setLoading(true);
    setError(null);

    try {
      // Fetch patient details
      console.log('Fetching patient details for:', patientId);
      const patient = await patientApi.get(patientId);
      console.log('Retrieved patient data:', patient);

      // Populate patient form data
      setPatientData({
        patient_id: patient.patient_id,
        first_name: patient.first_name,
        last_name: patient.last_name,
        date_of_birth: patient.date_of_birth ? (() => {
          const match = patient.date_of_birth.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          return match ? `${match[2]}/${match[3]}/${match[1]}` : patient.date_of_birth;
        })() : '',
        phone_number: patient.phone_number || '',
        gender: patient.gender || '',
        preferred_contact_method: patient.preferred_contact_method || 'sms',
        language: patient.language || 'en-US',
      });
      setIsExistingPatient(true);
      // PHA-1048: remember whose org this plan belongs to, so the VoP flag is resolved from
      // the patient rather than from wherever the clinic switcher happens to point.
      setPatientOrgId(
        String((patient as any)?.organization_id || (patient as any)?.organization || '') || null
      );

      // Fetch and pre-populate vitals
      try {
        const vitals = await patientApi.getVitals(patientId);
        if (vitals && Object.keys(vitals).length > 0) {
          const newVitals: Partial<VitalSigns> = { input_mode: 'height_weight' };
          if (vitals.blood_pressure_systolic) {
            newVitals.blood_pressure_systolic = String(Math.round(vitals.blood_pressure_systolic.value));
          }
          if (vitals.blood_pressure_diastolic) {
            newVitals.blood_pressure_diastolic = String(Math.round(vitals.blood_pressure_diastolic.value));
          }
          if (vitals.height) {
            const { feet, inches } = centimetersToFeetInches(vitals.height.value);
            newVitals.height_feet = String(feet);
            newVitals.height_inches = String(inches);
          }
          if (vitals.weight) {
            newVitals.weight_pounds = String(kilogramsToPounds(vitals.weight.value));
          }
          if (vitals.BMI) {
            newVitals.BMI = String(Math.round(vitals.BMI.value * 10) / 10);
          }
          setVitalSigns(prev => ({ ...prev, ...newVitals }));
        }
      } catch (e) {
        console.error('Failed to fetch vitals:', e);
      }

      // Fetch previously uploaded document names
      try {
        const docNames = await patientApi.getDocumentNames(patientId);
        setPreviousDocumentNames(docNames);
      } catch (e) {
        console.error('Failed to fetch document names:', e);
      }

      if (options?.regenerateCarePlan) {
        // ------------------------------------------------------------------
        // PHA-1033: care-plan REFRESH. Four sources, one shared review shape.
        // The first-time onboarding path is the code BELOW this block and is
        // deliberately untouched.
        // ------------------------------------------------------------------
        const source: CarePlanRefreshSource = options.regenerateSource || 'current';
        const patientName = `${patient.first_name} ${patient.last_name}`;

        // PHA-1046: the clinician's approve/reject decisions from the last review live only
        // in the candidate batch — `patient.problems` is approved-only, so a rejection is
        // simply absent there. A failed read is not fatal: everything then reads as
        // kept/new, which is the same behaviour as before this ticket.
        let priorCandidates: { problems: any[]; medications: any[] } = {
          problems: [],
          medications: [],
        };
        // PHA-1046 follow-up: the RAW most-recent batch, kept separately from the decision
        // set above. `patient.problems` is APPROVED-only, and an EHR-imported patient whose
        // item candidates were never passed through review has an approved care plan but an
        // empty approved item list (PHA-725 writes candidates approved=false/reviewed=false).
        // Found on dev: MRN 143138 has 6 problem + 6 medication candidates, all unreviewed,
        // and an active approved plan. Without this fallback source='current' opened Review
        // with "No problems extracted from documents" and an Approve button that could never
        // be satisfied — a dead end with no way forward.
        let rawCandidates: {
          problems: any[]; medications: any[]; problemsReviewed: boolean; medicationsReviewed: boolean;
        } = { problems: [], medications: [], problemsReviewed: false, medicationsReviewed: false };
        let priorBatchStamp = 0;
        try {
          const candidatesResponse = await patientApi.getCandidates(patientId);
          priorCandidates = readPriorCandidateDecisions(candidatesResponse);
          priorBatchStamp = getCandidateBatchStamp(candidatesResponse);
          rawCandidates = {
            problems: candidatesResponse?.problem_list?.extracted_data || [],
            medications: candidatesResponse?.medication_list?.extracted_data || [],
            problemsReviewed: candidatesResponse?.problem_list?.reviewed === true,
            medicationsReviewed: candidatesResponse?.medication_list?.reviewed === true,
          };
        } catch (e) {
          console.warn('Could not read prior candidate decisions for refresh:', e);
        }

        // PHA-1047: what the current approved plan actually covers. Read off the plan's own
        // attribution (icd10_codes[].clinical_disease plus the diseases[] on goals/questions/
        // tasks) rather than its `disease` column, which is the 'global' sentinel on every
        // merged plan and names no condition at all.
        const hasApprovedPlan = ((patient.care_plan_definitions || []) as any[]).length > 0;
        let currentPlanDiseases = collectClinicalDiseasesAcrossPlans(
          (patient.care_plan_definitions || []) as any[]
        );

        // PHA-1119 follow-up (Zack, patient m25tst100): for a patient still mid-onboarding,
        // "the current plan" is the unapproved DRAFT — `patient.care_plan_definitions` is the
        // approved list and is empty for them, so the condition checkboxes opened with nothing
        // ticked and the clinician's earlier choice (Hypertension + GERD) was silently
        // discarded. The ticket asks for the previous selection preserved AND editable, which
        // is exactly what pre-ticking gives: `getRefreshInitialSelectedDiseases` seeds the
        // boxes and the clinician can change them before generating.
        //
        // Only reached when there is no approved plan, so an enrolled patient's baseline is
        // untouched. Read the SAME manual-draft batch PatientDetail's CTA probes, so the
        // button that offered the regeneration and the selection it opens with agree.
        // Deliberately does NOT feed `hasApprovedPlan`: that field is what keeps first-time
        // program consent reachable for these patients (PHA-1073), and a draft is not
        // evidence of enrolment. A failed fetch leaves the selection empty — the pre-fix
        // behaviour — rather than blocking the regeneration.
        if (!hasApprovedPlan) {
          try {
            const draftPlans = await patientApi.getCarePlanCandidates(patientId, false, true);
            if (Array.isArray(draftPlans) && draftPlans.length > 0) {
              currentPlanDiseases = collectClinicalDiseasesAcrossPlans(draftPlans);
            }
          } catch (e) {
            console.warn('PHA-1119: could not read the draft plan to pre-select conditions:', e);
          }
        }

        const baseline = {
          approvedProblems: patient.problems || [],
          approvedMedications: patient.medications || [],
          priorProblemCandidates: priorCandidates.problems,
          priorMedicationCandidates: priorCandidates.medications,
          currentPlanDiseases,
          hasApprovedPlan,
        };
        setRefreshBaseline(baseline);

        /** Stamp the V / X / NEW state onto the sourced lists and open the wizard. */
        const enterRefreshReview = (
          rawProblems: any[],
          rawMedications: any[],
          step: number,
          message: string
        ) => {
          // PHA-1141: when the lists come from the EHR, what is NEW is an EHR change the
          // clinician must accept or reject — never pre-accepted.
          const newItemsUndecided = source === 'ehr' || source === 'ehr_changes';
          setExtractedData({
            problems: enrichProblemsWithICD10(
              buildRefreshReviewList({
                approved: baseline.approvedProblems,
                priorCandidates: baseline.priorProblemCandidates,
                sourced: deduplicateProblems(rawProblems),
                keyField: 'problem',
                newItemsUndecided,
              })
            ),
            medications: buildRefreshReviewList({
              approved: baseline.approvedMedications,
              priorCandidates: baseline.priorMedicationCandidates,
              sourced: rawMedications,
              keyField: 'medication',
              newItemsUndecided,
            }),
          });
          setDocumentsProcessed(true);
          setProcessedInputSnapshot(getInputSnapshot());
          setCarePlanData(null);
          setCarePlanGenerated(false);
          setCarePlanDirtySinceGeneration(false);
          localStorage.removeItem(`carePlanDirty_${patientId}`);
          setActiveStep(step);
          toast.success(message);
        };

        // --- 'upload': the clinician supplies new documents -----------------
        // PHA-1045: opens on Document Upload, but seeded with the chart's CURRENT lists
        // rather than empty. Two reasons, both about partial uploads: the clinician may only
        // have a new problem list, and `getDocumentUploadErrors` only permits a partial
        // upload once `documentsProcessed` is set — starting empty would demand both lists
        // and then leave medications blank, which fails the approve-time "at least one
        // medication" check. The upload handler replaces whichever list is actually parsed
        // and re-runs the diff against the same baseline.
        if (source === 'upload') {
          enterRefreshReview(
            baseline.approvedProblems,
            baseline.approvedMedications,
            getInitialStepForRefreshSource('upload'),
            `Upload ${patientName}'s new problem or medication list, then review before regenerating.`
          );
          setLoading(false);
          return;
        }

        // --- 'ehr': pull fresh data from the EHR, then review ---------------
        // PHA-1044: refreshFromEhr is fire-and-forget (202). Poll for a candidate batch
        // newer than the one that was already there, rather than guessing at a delay.
        if (source === 'ehr') {
          const orgId = (patient as any)?.organization_id || (patient as any)?.organization;
          setEhrRefreshBusy(true);
          try {
            try {
              await patientApi.refreshFromEhr({
                patient_id: patientId,
                ...(orgId ? { organization_id: String(orgId) } : {}),
              });
            } catch (triggerError: any) {
              const status = triggerError?.errorData?.status ?? triggerError?.status;
              // 409 means a sync is ALREADY running for this patient — its result is
              // exactly what we are waiting for, so fall through to the poll.
              if (status !== 409) {
                setError(
                  status === 403
                    ? 'You do not have access to refresh this patient from the EHR.'
                    : triggerError?.message || 'Failed to start the EHR refresh.'
                );
                toast.error('Could not start the EHR refresh.');
                setLoading(false);
                return;
              }
            }

            let refreshed: any = null;
            for (let attempt = 0; attempt < EHR_REFRESH_POLL_ATTEMPTS; attempt++) {
              await new Promise((resolve) =>
                setTimeout(resolve, EHR_REFRESH_POLL_INTERVAL_MS)
              );
              // The sync itself is server-side and keeps running; only this wizard's
              // interest in the result ends here.
              if (refreshAbandonedRef.current) return;
              try {
                const polled = await patientApi.getCandidates(patientId);
                if (getCandidateBatchStamp(polled) > priorBatchStamp) {
                  refreshed = polled;
                  break;
                }
              } catch (pollError) {
                // A transient blip mid-sync is expected; keep polling.
                console.warn('EHR refresh poll failed, retrying:', pollError);
              }
            }

            if (refreshAbandonedRef.current) return;

            if (!refreshed) {
              // Not an error — the sync is still running and will surface in the Smart Hub.
              // Nothing has been generated, so the existing care plan is untouched.
              // PHA-1072: the poll cannot tell "still running" from "finished, found
              // nothing", and the second case is now reachable — an image-onboarded chart
              // in an EHR clinic may have an MRN the EHR does not know, in which case the
              // sync completes without writing a new candidate batch and we land here.
              // Name that possibility instead of implying the wait is always the reason.
              setError(
                'No refreshed data has come back from the EHR yet. The sync may still be running — the new data will appear in the Smart Hub, and you can regenerate once it does. If it never arrives, this patient may have no matching record in the EHR; regenerate from the existing lists or from documents instead.'
              );
              toast('EHR refresh still running — try again shortly.', { icon: '⏳' });
              setLoading(false);
              return;
            }

            enterRefreshReview(
              refreshed.problem_list?.extracted_data || [],
              refreshed.medication_list?.extracted_data || [],
              getInitialStepForRefreshSource('ehr'),
              `Imported ${patientName}'s latest problem and medication lists from the EHR — review, then regenerate.`
            );
          } finally {
            setEhrRefreshBusy(false);
            setLoading(false);
          }
          return;
        }

        // --- 'documents': re-parse the originally-uploaded images -----------
        // PHA-863 Mode B. NOTE: re-parsing reproduces the onboarding-day extraction, so
        // edits made to the lists since then are not preserved — which is why 'current'
        // remains on offer for non-EHR patients too (PHA-1043).
        if (source === 'documents') {
          try {
            const reparsed = await patientApi.reparseOriginalDocuments(patientId);
            const docs: any[] = reparsed?.documents || [];
            const rawProblems = docs.find((d) => d.document_type === 'problem_list')?.extracted_items || [];
            const rawMedications = docs.find((d) => d.document_type === 'medication_list')?.extracted_items || [];
            if (rawProblems.length === 0 && rawMedications.length === 0) {
              setError(
                'No original documents were found to re-parse for this patient. Regenerate from the current data instead.'
              );
              toast.error('No original documents found to re-parse.');
              setLoading(false);
              return;
            }
            enterRefreshReview(
              rawProblems,
              rawMedications,
              getInitialStepForRefreshSource('documents'),
              `Re-extracted ${patientName}'s problem and medication lists from the original documents — review, then regenerate.`
            );
          } catch (e: any) {
            console.error('Failed to re-parse original documents:', e);
            setError(
              'Failed to re-parse the original documents. Please try again, or regenerate from the current data.'
            );
            toast.error('Could not re-parse the original documents.');
          }
          setLoading(false);
          return;
        }

        // --- 'ehr_changes': the Smart Hub's Re-generate on an EHR change ---
        // PHA-1141: review what the nightly EHR sync last pulled against the approved plan —
        // no second EHR pull, the sync already has it. buildRefreshReviewList marks each item
        // kept / previously rejected / NEW, and NEW items start undecided: the ticket says the
        // changes are reviewed, "not accepted automatically". With no stored pull (a clinic
        // whose sync has not run) this falls through to 'current' rather than failing.
        if (source === 'ehr_changes') {
          let latestBatch: { problems: any[]; medications: any[] } | null = null;
          try {
            latestBatch = (await patientApi.getEhrChanges(patientId))?.latest_ehr_batch || null;
          } catch (e) {
            console.warn('PHA-1141: could not read the latest EHR pull; regenerating from the chart:', e);
          }
          if (latestBatch && (latestBatch.problems.length > 0 || latestBatch.medications.length > 0)) {
            enterRefreshReview(
              latestBatch.problems,
              latestBatch.medications,
              getInitialStepForRefreshSource('ehr_changes'),
              `Review ${patientName}'s EHR changes — accept or reject each item marked NEW — then regenerate.`
            );
            setLoading(false);
            return;
          }
        }

        // --- 'current' (default): regenerate from what is on the chart ------
        // PHA-863 Mode A. `sourced` is normally the approved list, so every item comes back
        // 'kept'; the diff still runs because it is what carries previous REJECTIONS through
        // as X (they exist nowhere in patient.problems).
        //
        // PHA-1046 follow-up: fall back to the most recent candidate batch when the approved
        // list is empty. "The data already on the chart" is what the clinician means, and for
        // a patient whose items were never reviewed that data lives in the candidates, not in
        // the approved list. applyCandidateApprovalDefaults keeps the PHA-1029 rule: an
        // UNREVIEWED batch carries no clinician decision, so it comes in accepted.
        const currentProblems = baseline.approvedProblems.length > 0
          ? baseline.approvedProblems
          : applyCandidateApprovalDefaults(rawCandidates.problems, rawCandidates.problemsReviewed);
        const currentMedications = baseline.approvedMedications.length > 0
          ? baseline.approvedMedications
          : applyCandidateApprovalDefaults(rawCandidates.medications, rawCandidates.medicationsReviewed);

        enterRefreshReview(
          currentProblems,
          currentMedications,
          getInitialStepForRefreshSource('current'),
          `Select conditions to regenerate the care plan for ${patientName}`
        );
        setLoading(false);
        return;
      }

      // PHA-1066 / PHA-1064: "Complete Care Plan Regeneration" (PatientDetail.tsx) routes here —
      // through continuingOnboardingPatientId, never through the REGENERATE_PATIENT_ID_
      // STORAGE_KEY branch above — whenever a manual draft is already in flight
      // (hasManualDraftCarePlan === true, same check PatientDetail's CTA uses). One probe with
      // three consumers below:
      //   * PHA-1066: mark the session as a refresh so Step 4 renders the Cancel button;
      //   * PHA-1064: tell the "already has a care plan" branch that this is a regeneration to
      //     resume, not a finished onboarding to congratulate;
      //   * scope the draft preload to the clinician's own batch (see getCarePlanCandidates below).
      let manualDraftInFlight = false;
      try {
        const manualDraftCandidates = await patientApi.getCarePlanCandidates(patientId, false, true);
        manualDraftInFlight = Array.isArray(manualDraftCandidates) && manualDraftCandidates.length > 0;
      } catch (e) {
        // A failed probe is not "no draft" — but the only safe default is to treat it as none,
        // since every consumer below widens behaviour rather than blocking it.
        console.error('PHA-1066: manual draft state check failed:', e);
      }
      // PHA-1073: only consumer #1 is gated on an APPROVED plan. `refreshSource` is what
      // makes CarePlanApproval render as a REFRESH — which swaps the first-time consent
      // capture for the PHA-726/897 "reviewed with patient" acknowledgment ("the patient's
      // original program consent is unchanged") and stages the item review instead of
      // approving it. Both are correct for an enrolled patient and wrong for one who has
      // never been onboarded: a draft-only patient would reach Approve with no way to
      // record program consent. Consumers #2 and #3 stay on `manualDraftInFlight` alone —
      // they are about resuming an in-flight draft, which is right either way.
      const hasApprovedCarePlan =
        !!patient.care_plan_definitions && patient.care_plan_definitions.length > 0;
      // PHA-1067 follow-up: ONE flag for "this re-entry is a regeneration to resume". It has to
      // be the same condition everywhere, because the three things it drives have to agree:
      // restoring the staged review, staging (not approving) the next review, and generating
      // from the staged set. Restoring staged decisions into a session that then commits them
      // through approveItems and generates from the live lists would be the original PHA-1067
      // bug, re-entered through the resume door.
      const resumeAsRefresh = shouldResumeAsRefresh({ manualDraftInFlight, hasApprovedCarePlan });
      if (resumeAsRefresh) {
        setRefreshSource('current');
      }

      // Try to fetch candidates for review
      let problemCandidates: any[] = [];
      let medicationCandidates: any[] = [];
      let hasNewCandidates = false;
      // PHA-1029: hoisted out of the try so the branches below can gate the approval default
      // on them. A failed candidates fetch leaves both false, which is the safe reading —
      // nothing was reviewed because nothing was read.
      let problemReviewed = false;
      let medicationReviewed = false;
      // PHA-1067 follow-up: the batch stamp each `reviewed` flag belongs to, so a staged review
      // can be matched against the batch it was built from.
      let problemBatchTs: any = null;
      let medicationBatchTs: any = null;

      try {
        console.log('Fetching candidates for patient:', patientId);
        const candidatesResponse = await patientApi.getCandidates(patientId);
        console.log('Candidates response:', candidatesResponse);
        console.log('Candidates response type:', typeof candidatesResponse);
        console.log('Candidates response keys:', Object.keys(candidatesResponse || {}));

        // Extract candidates from response
        if (candidatesResponse.problem_list?.extracted_data) {
          problemCandidates = candidatesResponse.problem_list.extracted_data;
          console.log('Found problem candidates:', problemCandidates.length);
        }
        if (candidatesResponse.medication_list?.extracted_data) {
          medicationCandidates = candidatesResponse.medication_list.extracted_data;
          console.log('Found medication candidates:', medicationCandidates.length);
        }

        // Only treat candidates as "new" if the most-recent batch has not been reviewed yet.
        // A batch where the user rejected some items is still "reviewed" and should not re-trigger step 2.
        problemReviewed = candidatesResponse.problem_list?.reviewed === true;
        medicationReviewed = candidatesResponse.medication_list?.reviewed === true;
        problemBatchTs = candidatesResponse.problem_list?.attributed_ts ?? null;
        medicationBatchTs = candidatesResponse.medication_list?.attributed_ts ?? null;
        hasNewCandidates =
          (problemCandidates.length > 0 && !problemReviewed) ||
          (medicationCandidates.length > 0 && !medicationReviewed);
        console.log('Has new candidates:', hasNewCandidates, { problemReviewed, medicationReviewed });

        // Log the structure for debugging
        if (candidatesResponse.problem_list) {
          console.log('Problem list structure:', candidatesResponse.problem_list);
        }
        if (candidatesResponse.medication_list) {
          console.log('Medication list structure:', candidatesResponse.medication_list);
        }
      } catch (candidateError: any) {
        console.error('Failed to fetch candidates - full error:', candidateError);
        console.error('Error message:', candidateError.message);
        console.error('Error status:', candidateError.status);
        // Continue with existing logic even if candidates fetch fails
        hasNewCandidates = false;
      }

      // PHA-1067 follow-up: read the staged review BEFORE deciding where to resume.
      //
      // It used to be read inside the care-plan branch below, which is only reached when
      // `hasNewCandidates` is false — and for a regeneration sourced from the EHR it never is.
      // refreshFromEhr inserts an unreviewed candidate batch at the start of the regeneration,
      // the Review step no longer marks it reviewed (it stages instead), so that batch stays
      // unreviewed forever and re-entry restarted the wizard at Review with the raw imported
      // list. The staged review — the clinician's actual decisions, and the list the draft plan
      // on screen was generated from — was never consulted.
      let stagedProblems: any[] = [];
      let stagedMedications: any[] = [];
      if (resumeAsRefresh) {
        try {
          const stagedReview = await patientApi.getItemReviewDraft(patientId);
          stagedProblems = stagedReview?.problem_list?.extracted_data || [];
          stagedMedications = stagedReview?.medication_list?.extracted_data || [];
        } catch (e) {
          // Falling back to the candidates below is the pre-PHA-1067 behaviour: the review
          // reads as the previous approved one. Wrong, but never destructive.
          console.warn('PHA-1067: could not read the staged item review:', e);
        }
      }
      const stagedCoversProblems = stagedReviewCoversCandidateBatch(stagedProblems, problemBatchTs);
      const stagedCoversMedications = stagedReviewCoversCandidateBatch(
        stagedMedications,
        medicationBatchTs
      );
      if (hasNewCandidates && (stagedCoversProblems || stagedCoversMedications)) {
        // Re-decide per document type, NOT by clearing the flag wholesale: `hasNewCandidates`
        // is an OR across the two lists (the PHA-1029 trap), so a genuinely new medication
        // batch must still open Review even when the problem list is covered by staging.
        hasNewCandidates =
          (problemCandidates.length > 0 && !problemReviewed && !stagedCoversProblems) ||
          (medicationCandidates.length > 0 && !medicationReviewed && !stagedCoversMedications);
        console.log('Has new candidates after the staged review:', hasNewCandidates, {
          stagedCoversProblems,
          stagedCoversMedications,
        });
      }

      // If no new candidates are available
      if (!hasNewCandidates) {
        // Check if patient already has approved items and care plans
        const hasProblems =
          (patient.problems && patient.problems.length > 0) || problemCandidates.length > 0;
        const hasMedications =
          (patient.medications && patient.medications.length > 0) || medicationCandidates.length > 0;
        const hasCarePlans = patient.care_plan_definitions && patient.care_plan_definitions.length > 0;
        const hasAnyItems = hasProblems || hasMedications;

        console.log('Patient state analysis:', {
          hasProblems,
          hasMedications,
          hasCarePlans,
          hasAnyItems,
          problemCount: patient.problems?.length || 0,
          medicationCount: patient.medications?.length || 0,
          carePlanCount: patient.care_plan_definitions?.length || 0
        });

        // PHA-1064: this used to be `if (hasCarePlans) setActiveStep(4)` — an unescapable
        // "Generating Survey Questions" spinner with no request behind it (see
        // resolveResumeDestination). That is exactly the report: patient m22tst100, which has
        // 1 approved care plan AND 1 manual draft in flight, clicking "Complete Care Plan
        // Regeneration".
        const destination = resolveResumeDestination({
          hasCarePlans: !!hasCarePlans,
          hasAnyItems,
          manualDraftInFlight,
        });
        console.log('Resume destination:', destination);

        if (destination === 'chart') {
          console.log('Patient already has care plans and no draft in flight, handing over the chart');
          toast.success(`${patient.first_name} ${patient.last_name} already has a care plan. Onboarding is complete.`);
          // Hand over the chart rather than parking the clinician on a completion step they
          // never asked for. Identity comes from the freshly-fetched `patient`, NOT patientData:
          // setPatientData above has not flushed into this closure yet, so exitToPatientChart()
          // would read an empty patient_id and silently degrade to the patient list.
          if (onExitToPatientChart) {
            resetWorkflow({ navigateHome: false });
            onExitToPatientChart({
              patientId: patient.patient_id,
              firstName: patient.first_name || undefined,
              lastName: patient.last_name || undefined,
              mrn: patient.patient_id,
            });
          } else {
            resetWorkflow();
          }
          setLoading(false);
          return;
        } else if (destination === 'care-plan') {
          // A manual draft to resume, or approved items still waiting for a plan.
          console.log('Resuming at the care plan step');

          // PHA-1067: a regeneration's review is staged, not committed, so when we are resuming
          // one the candidates below still describe the PREVIOUS approved review — resuming off
          // them would show the clinician a list that contradicts the draft plan on screen, and
          // "Save as Draft" would quietly lose everything they decided. The staged set is the
          // record of that session; it is read above, before the resume destination is decided.

          // Use candidates if available (preserves both approved and rejected items with their status),
          // otherwise fall back to patient.problems/medications (approved only)
          const problems = stagedProblems.length > 0
            ? enrichProblemsWithICD10(stagedProblems)
            : problemCandidates.length > 0
              ? enrichProblemsWithICD10(applyCandidateApprovalDefaults(problemCandidates, problemReviewed))
              : enrichProblemsWithICD10(patient.problems ? patient.problems.map((p: any) => ({ ...p, approved: true })) : []);
          const medications = stagedMedications.length > 0
            ? stagedMedications
            : medicationCandidates.length > 0
              ? applyCandidateApprovalDefaults(medicationCandidates, medicationReviewed)
              : patient.medications ? patient.medications.map((m: any) => ({ ...m, approved: true })) : [];

          setExtractedData({ problems, medications });
          setDocumentsProcessed(true);
          setProcessedInputSnapshot(getInputSnapshot());

          // Try to pre-load any previously generated draft care plan so the user doesn't need to regenerate.
          // PHA-1064: when we got here BECAUSE a manual draft is in flight, scope the preload to
          // manual batches. The unscoped form also returns the nightly ehr_cron review queue
          // (PHA-397/510) — a different surface — which would mix cron-generated content into the
          // clinician's own regeneration and hand handleCancelRegeneration a cron uploaded_ts to
          // scope its discard by. With no manual draft the call is unchanged.
          try {
            const draftCandidates = await patientApi.getCarePlanCandidates(patientId, false, manualDraftInFlight);
            if (draftCandidates && draftCandidates.length > 0) {
              console.log('Found draft care plan candidates, pre-loading for review');
              setCarePlanData({ care_plan_definitions: draftCandidates, surveys: [] });
              setCarePlanGenerated(true);
              // PHA-945 used to arm an unconditional "this draft may be stale, re-generate
              // it" callout here for draft-only patients. PHA-1119 removed it: re-generating
              // from inside the resume path skipped the Upload and condition-selection steps.
              // The chart's own "Regenerate Care Plan" button now covers that, through the
              // full source-picker flow. Resuming means resuming.
              //
              // PHA-815: cadence now comes from each question's backend computed_cadence;
              // no client-side org-template fetch / re-derivation needed.
              if (localStorage.getItem(`carePlanDirty_${patientId}`) === 'true') {
                setCarePlanDirtySinceGeneration(true);
              }
              toast.success(`Continuing onboarding for ${patient.first_name} ${patient.last_name} - returning to care plan review`);
            } else {
              toast.success(`Continuing onboarding for ${patient.first_name} ${patient.last_name} - generating care plan`);
            }
          } catch (draftError) {
            console.warn('Could not load draft care plan candidates:', draftError);
            toast.success(`Continuing onboarding for ${patient.first_name} ${patient.last_name} - generating care plan`);
          }

          // Pre-populate consent status if previously recorded
          if (patient.consent_status === 'denied') {
            setLoadedConsentStatus('no');
          } else if (patient.consent_status === 'collected') {
            setLoadedConsentStatus('yes');
          }

          // Jump directly to care plan step (step 3)
          setActiveStep(3);
        } else {
          // No items at all - need to upload documents first
          console.log('No items found, redirecting to document upload');
          setActiveStep(1); // Redirect to document upload
        }

        setLoading(false);
        return;
      }

      // We have new candidates to review
      console.log('Found new candidates, setting up approval workflow');
      // Compare candidates with approved items for diff highlighting
      const approvedProblems = patient.problems || [];
      const approvedMedications = patient.medications || [];

      // PHA-1029: stamp the batch default BEFORE the diff. compareItems no longer decides
      // approval itself, and `reviewed` is read per document type — hasNewCandidates is an OR
      // across the two, so a half-finished review (problems approved, medications abandoned)
      // used to send the reviewed problem list through here and resurrect its rejections.
      const dedupedCandidates = applyCandidateApprovalDefaults(
        deduplicateProblems(problemCandidates),
        problemReviewed
      );
      const medicationCandidatesWithDefaults = applyCandidateApprovalDefaults(
        medicationCandidates,
        medicationReviewed
      );
      // PHA-1067 follow-up (Zack): when we open Review because a nightly EHR import landed a NEWER
      // batch than the saved draft, lay the clinician's staged decisions back over the imported
      // list rather than discarding them. A matched item keeps their accept/reject and edits, an
      // item only in the import is surfaced as new, and a decision on an item the import no longer
      // lists is kept — so a rejected medication stays rejected across the import instead of
      // silently coming back. `stagedProblems` / `stagedMedications` are empty for a first review
      // or a cron review queue, so this is a no-op there.
      const comparedProblems = overlayStagedDecisions(
        compareItems(approvedProblems, dedupedCandidates, 'problem'),
        stagedProblems,
        approvedProblems,
        'problem'
      );
      const comparedMedications = overlayStagedDecisions(
        compareItems(approvedMedications, medicationCandidatesWithDefaults, 'medication'),
        stagedMedications,
        approvedMedications,
        'medication'
      );

      // Set extracted data with compared items (all marked for re-review)
      setExtractedData({
        problems: enrichProblemsWithICD10(comparedProblems),
        medications: comparedMedications
      });
      setDocumentsProcessed(true);
      setProcessedInputSnapshot(getInputSnapshot());

      // Jump to review step (step 2)
      setActiveStep(2);

      toast.success(`Continuing onboarding for ${patient.first_name} ${patient.last_name} - found new items to review`);
    } catch (err: any) {
      setError(`Failed to load patient data: ${err.message}`);
      toast.error(`Failed to load patient data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }; const generatePatientId = () => {
    const timestamp = Date.now().toString().slice(-6);
    const randomNum = Math.floor(Math.random() * 100).toString().padStart(2, '0');
    const generatedId = `MRN${timestamp}${randomNum}`;
    setPatientData(prev => ({ ...prev, patient_id: generatedId }));
  };

  // `updateIfExists` is set only by the "Patient Already Exists" dialog below: the first
  // press imports, and if the MRN turns out to be in Phanova already the clinician is asked
  // whether to overwrite it with the EHR's version, which re-enters here with the flag. Same
  // function on purpose — the retry has to reuse the missing-phone prompt and the form
  // population, which is the whole body underneath.
  const handleImportFromEhr = async (options?: { updateIfExists?: boolean }) => {
    const mrn = patientData.patient_id?.trim();
    if (!mrn) {
      setFieldErrors(prev => ({ ...prev, patient_id: 'MRN is required for EHR import' }));
      return;
    }
    // PHA-958: once the EHR has told us it holds no phone for this chart, validate the
    // clinician's number here rather than spending another EHR round trip to be told the
    // same thing. Same rule as the create-patient path below.
    const typedPhone = patientData.phone_number?.trim().replace(/[\s\-\(\)]/g, '') || '';
    const phoneIsWellFormed = /^(\d{10}$)|(^\+[1-9]\d{1,14}$)/.test(typedPhone);
    if (ehrPhoneRequired) {
      if (!typedPhone) {
        setFieldErrors(prev => ({
          ...prev,
          phone_number: 'Phone number is required to import this patient from the EHR.',
        }));
        return;
      }
      if (!phoneIsWellFormed) {
        setFieldErrors(prev => ({
          ...prev,
          phone_number: 'Phone number must be 10 digits (e.g. 5551234567) for US numbers or include a +(country code) for international numbers (e.g. +442038078196)',
        }));
        return;
      }
    }
    setEhrImportLoading(true);
    setError(null);
    try {
      // Only forward a well-formed number: the EHR's own phone wins anyway, and a
      // half-typed one would come back a 400 for a chart that imports perfectly well.
      const result = await patientApi.importFromEhr(
        mrn,
        phoneIsWellFormed ? typedPhone : undefined,
        options?.updateIfExists,
      );

      // PHA-958: the EHR chart carries no phone and none was typed. This is a 200, not an
      // error: nothing was created, so populate what the EHR DID return, leave the form
      // editable and mark phone as the one thing still needed.
      if (result.needs_phone_number) {
        const missingDemo = result.demographics || {};
        const missingDobParts = missingDemo.date_of_birth?.split('-');
        setPatientData(prev => ({
          ...prev,
          patient_id: missingDemo.patient_id || mrn,
          first_name: missingDemo.first_name || '',
          last_name: missingDemo.last_name || '',
          date_of_birth: missingDobParts
            ? `${missingDobParts[1]}/${missingDobParts[2]}/${missingDobParts[0]}`
            : '',
          // phone_number deliberately left as-is: we only get here when it was blank or
          // malformed, and clearing a half-typed number would throw away the clinician's
          // work right as we ask them to correct it.
          gender: (missingDemo.gender || '').toLowerCase(),
        }));
        setEhrPhoneRequired(true);
        setFieldErrors(prev => ({
          ...prev,
          phone_number: 'The EHR has no phone number for this patient. Enter one, then press Import from EHR again to finish.',
        }));
        setError(null);
        toast('The EHR has no phone number on file — enter one to finish importing.', {
          icon: '📞',
          duration: 6000,
        });
        return;
      }

      const demo = result.demographics;
      const dobParts = demo.date_of_birth?.split('-');
      const formattedDob = dobParts ? `${dobParts[1]}/${dobParts[2]}/${dobParts[0]}` : '';
      setPatientData({
        patient_id: demo.patient_id || mrn,
        first_name: demo.first_name || '',
        last_name: demo.last_name || '',
        date_of_birth: formattedDob,
        phone_number: demo.phone_number || '',
        gender: (demo.gender || '').toLowerCase(),
        preferred_contact_method: demo.preferred_contact_method || 'sms',
        language: demo.language || 'en-US',
      });
      setEhrPhoneRequired(false);
      setFieldErrors(prev => ({ ...prev, phone_number: undefined }));
      setEhrImported(true);
      setIsExistingPatient(true);
      // PHA-1117: an EHR import creates the patient without going through
      // loadExistingPatientData, so pin the wizard to the clinic the import actually filed
      // them under — otherwise the care plan review cycle (and the org flags keyed off
      // patientOrgId) resolve as if no patient were loaded.
      await syncPatientOrgIdFromRecord(demo.patient_id || mrn);
      // `created === false` means this was the confirmed update of an MRN already in
      // Phanova. Surface the match card so the clinician can jump straight to the existing
      // chart — the wizard is about to walk them through re-reviewing a patient they may
      // already have onboarded.
      if (!result.created) {
        setExistingPatientMatch({
          patient_id: demo.patient_id || mrn,
          first_name: demo.first_name || '',
          last_name: demo.last_name || '',
          date_of_birth: formattedDob,
          mrn: demo.patient_id || mrn,
        });
      }
      setEhrImportResult({
        created: result.created,
        candidates_added: result.candidates_added,
        problems_count: result.problems_count,
        medications_count: result.medications_count,
        vitals_count: result.vitals_count,
      });
      toast.success(
        result.created
          ? `Patient imported. ${result.problems_count} problems, ${result.medications_count} medications, ${result.vitals_count} vitals imported.`
          : `Existing patient updated from EHR. ${result.candidates_added} candidate items added.`
      );
    } catch (err: any) {
      const errorMessage = err?.message || err?.errorData?.error || 'Failed to import from EHR';
      // Existing MRN — ask before overwriting, the same way the manual create path does.
      // Backend rejects fresh imports of an existing MRN with the literal
      // "already exists for organization …" message raised at
      // app_logic.py:import_patient_from_ehr. If the wording there changes, update this
      // regex to match.
      //
      // This used to fire `refreshFromEhr` silently instead (PHA-397). Two problems: the
      // clinician was never asked before another clinic's chart overwrote a patient they
      // had hand-entered, and that route is an ASYNC background sync — it returns 202 and
      // the wizard could show nothing of what the EHR actually holds. Re-importing with
      // update_if_exists is synchronous, so the confirmed answer lands in the form and the
      // candidate items reach step 3 (Review) where they get approved.
      //
      // `!options?.updateIfExists` guards the confirmed retry: if the update path somehow
      // still reports a duplicate, show it rather than re-opening the same dialog forever.
      const isDuplicateMrn = /already exists/i.test(errorMessage);
      if (isDuplicateMrn && !options?.updateIfExists) {
        setPendingEhrImportMrn(mrn);
        setDuplicateDialogMessage(
          `Patient with MRN "${mrn}" already exists. Do you want to update the existing ` +
          `patient with the details from the EHR? This replaces their demographics with ` +
          `the EHR's and adds the EHR's problems and medications for review.`
        );
        setShowDuplicateDialog(true);
        setError(null);
        return;
      }
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setEhrImportLoading(false);
    }
  };

  const handleRejectEhrImport = async () => {
    if (!patientData.patient_id) return;
    setLoading(true);
    try {
      const result = await patientApi.cancelImportFromEhr(patientData.patient_id);
      if (result.deleted) {
        toast.success('Imported patient data has been rejected.');
        setEhrImported(false);
        setEhrImportResult(null);
        setIsExistingPatient(false);
      } else {
        toast.error(result.reason || 'Could not reject import.');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to reject import');
    } finally {
      setLoading(false);
    }
  };

  const processDocumentsWithProgress = async (files: UploadedFile[]): Promise<{
    problems: ParsedProblem[],
    medications: ParsedMedication[],
    // PHA-1079: why a file failed, per section. Until now this only ever reached the per-file
    // progress card, so when a run extracted nothing the field underneath fell back to generic
    // "add / upload relevant information" — advice about input that was never the problem. The
    // report is exactly that: the card showed Azure "401 - Access denied", the field did not.
    // Reported only, never blocking: the caller uses it to WORD the zero-extraction error, so a
    // failed file alongside input that did parse still goes through as before.
    errors: { problemList?: string; medicationList?: string },
  }> => {
    if (files.length === 0) return { problems: [], medications: [], errors: {} };

    // Initialize progress tracking
    const fileProgress = files.map(file => ({
      name: file.file.name,
      status: 'pending' as const,
      progress: 0
    }));

    setUploadProgress({
      files: fileProgress,
      overallProgress: 0,
      currentFile: files[0]?.file.name
    });
    setUploadModalOpen(true);

    try {
      const allExtractedProblems: ParsedProblem[] = [];
      const allExtractedMedications: ParsedMedication[] = [];
      const fileErrors: { problemList?: string; medicationList?: string } = {};

      for (let i = 0; i < files.length; i++) {
        const uploadedFile = files[i];
        const file = uploadedFile.file;

        // Which section this file belongs to. Hoisted out of the try so the catch below can
        // attribute the failure — it is also what decides where the extracted items land.
        const isProblemFile =
          problemListFiles.some(f => f.file === file) || pastedProblemImages.some(f => f.file === file);
        const documentType = isProblemFile ? 'problem_list' : 'medication_list';

        // Update current file status to uploading
        setUploadProgress(prev => ({
          ...prev,
          currentFile: file.name,
          files: prev.files.map((f, idx) =>
            idx === i ? { ...f, status: 'uploading' as const, progress: 0 } : f
          )
        }));

        try {
          // The time-based TimedProgress bar (rendered in the loading card) conveys
          // progress now, so we no longer fake a per-file 0→90% tick here.
          // Use the fileToPayload helper function
          const payload = await fileToPayload(file);

          const body = {
            patient_id: patientData.patient_id,
            document_type: documentType,
            attributed_ts: new Date().toISOString(),
            document_name: file.name,
            ...payload, // includes (bytes + mime_type) OR (content + mime_type)
          };

          // Make the API call
          const response = await apiRequest('/session/patient/raw_document/parse', {
            method: 'POST',
            body: JSON.stringify(body),
          });

          const data = await response.json();

          // Collect extracted items
          if (data.extracted_items) {
            if (documentType === 'problem_list') {
              allExtractedProblems.push(...data.extracted_items);
            } else {
              allExtractedMedications.push(...data.extracted_items);
            }
          }

          // Mark file as completed
          setUploadProgress(prev => ({
            ...prev,
            files: prev.files.map((f, idx) =>
              idx === i ? { ...f, status: 'completed' as const, progress: 100 } : f
            ),
            overallProgress: ((i + 1) / files.length) * 100
          }));

        } catch (error) {
          console.error(`Error processing file ${file.name}:`, error);

          // PHA-1079: keep the FIRST failure per section. With several files in one section the
          // last one would otherwise win, and the first is the one whose card the clinician is
          // already looking at.
          const failureMessage = error instanceof Error ? error.message : 'Upload failed';
          const section = documentType === 'problem_list' ? 'problemList' : 'medicationList';
          if (!fileErrors[section]) {
            fileErrors[section] = failureMessage;
          }

          // Mark file as error
          setUploadProgress(prev => ({
            ...prev,
            files: prev.files.map((f, idx) =>
              idx === i ? {
                ...f,
                status: 'error' as const,
                progress: 0,
                error: error instanceof Error ? error.message : 'Upload failed'
              } : f
            )
          }));
        }
      }

      // Wait a bit before closing modal
      await new Promise(resolve => setTimeout(resolve, 1000));
      setUploadModalOpen(false);

      return {
        problems: allExtractedProblems,
        medications: allExtractedMedications,
        errors: fileErrors
      };

    } catch (error) {
      console.error('Error in document processing:', error);
      setUploadModalOpen(false);
      throw error;
    }
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      // If EHR import was completed, skip document upload and go to review
      if (ehrImported) {
        setError(null);
        try {
          const candidatesResponse = await patientApi.getCandidates(patientData.patient_id!);
          const problems = candidatesResponse.problem_list?.extracted_data || [];
          const medications = candidatesResponse.medication_list?.extracted_data || [];
          // PHA-1029: a fresh EHR import is unreviewed, so it comes in accepted. Gated per
          // document type rather than forced: when the EHR pull returns nothing new no rows are
          // inserted, getCandidates hands back the PREVIOUS reviewed batch, and force-approving
          // there would resurrect the items the clinician had rejected.
          const problemsReviewed = candidatesResponse.problem_list?.reviewed === true;
          const medicationsReviewed = candidatesResponse.medication_list?.reviewed === true;
          const dedupedProblems = deduplicateProblems(problems);
          setExtractedData({
            problems: enrichProblemsWithICD10(
              applyCandidateApprovalDefaults(dedupedProblems, problemsReviewed)
            ),
            medications: applyCandidateApprovalDefaults(medications, medicationsReviewed),
          });
        } catch (err) {
          console.error('Failed to load EHR candidates:', err);
        }
        setDocumentsProcessed(true);
        setActiveStep(2);
        return;
      }

      // Validate patient information with trimmed values
      const trimmedPatientData = {
        patient_id: patientData.patient_id?.trim(),
        first_name: patientData.first_name?.trim(),
        last_name: patientData.last_name?.trim(),
        date_of_birth: patientData.date_of_birth?.trim(),
        phone_number: patientData.phone_number?.trim().replace(/[\s\-\(\)]/g, '')
      };

      // Validate date format (MM/DD/YYYY) and convert to ISO (YYYY-MM-DD)
      let isoDateOfBirth = '';
      const datePattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
      const dateMatch = trimmedPatientData.date_of_birth?.match(datePattern);

      if (dateMatch && isValidDate(trimmedPatientData.date_of_birth)) {
        const [, month, day, year] = dateMatch;
        isoDateOfBirth = `${year}-${month}-${day}`;
      }

      // Track which fields are missing or invalid with specific error messages
      const errors: { [key: string]: string | undefined } = {};
      
      if (!trimmedPatientData.patient_id) {
        errors.patient_id = 'MRN is required';
      }
      if (!trimmedPatientData.first_name) {
        errors.first_name = 'First name is required';
      }
      if (!trimmedPatientData.last_name) {
        errors.last_name = 'Last name is required';
      }
      if (!trimmedPatientData.date_of_birth) {
        errors.date_of_birth = 'Date of birth is required';
      } else if (!isoDateOfBirth) {
        errors.date_of_birth = 'Invalid date (use MMDDYYYY format, e.g., 01012000)';
      }
      if (!trimmedPatientData.phone_number) {
        errors.phone_number = 'Phone number is required';
      } else if (!/^(\d{10}$)|(^\+[1-9]\d{1,14}$)/.test(trimmedPatientData.phone_number)) {
        errors.phone_number = 'Phone number must be 10 digits (e.g. 5551234567) for US numbers or include a +(country code) for international numbers (e.g. +442038078196)';
      }
      if (!patientData.gender) {
        errors.gender = 'Gender is required';
      }

      setFieldErrors(errors);

      // Check if any field has an error and build dynamic summary message
      const errorMessages = Object.values(errors).filter(Boolean) as string[];
      if (errorMessages.length > 0) {
        setError(`Please correct the following: ${errorMessages.join(', ')}`);
        return;
      }

      // Create or update patient
      setLoading(true);
      try {
        let result: any;
        if (isExistingPatient) {
          // Patient loaded from continuation — update and advance
          result = await patientApi.update({
            patient_id: patientData.patient_id,
            first_name: patientData.first_name?.trim(),
            last_name: patientData.last_name?.trim(),
            date_of_birth: isoDateOfBirth,
            phone_number: patientData.phone_number?.trim().replace(/[\s\-\(\)]/g, ''),
            gender: patientData.gender,
          });
        } else {
          // Send the clinic the user actually has selected. Without this the
          // server falls back to req.session.currentOrganizationId — a SHARED,
          // server-side value common to every tab, while this selector is
          // per-tab React state. That divergence wrote a patient created under
          // "CitiWide Health" into "SMC" in prod on 2026-08-10.
          // 'ALL' is deliberately not sent: a patient belongs to one clinic, and
          // the backend rejects an ambiguous target rather than guessing.
          result = await patientApi.create({
            ...patientData,
            date_of_birth: isoDateOfBirth, // Use ISO format for backend
            hashed_user_ids: [],
            ...(selectedOrgId && selectedOrgId !== 'ALL'
              ? { organization_id: selectedOrgId }
              : {}),
          });
        }
        // PHA-1117: the patient row now exists, so pin the wizard to ITS clinic. Awaited rather
        // than fired-and-forgotten: this runs at Step 1 and CarePlanApproval does not mount until
        // Step 4, so the review cycle has resolved long before its review-date field is seeded.
        await syncPatientOrgIdFromRecord(patientData.patient_id);
        // Check phone verification result
        setPhoneTypeWarning(null);
        if (result?.phone_type === 'landline') {
          setPatientData(prev => ({ ...prev, preferred_contact_method: 'voice' }));
          if (!voiceEnabled) {
            const msg = 'This phone number is a landline. SMS is unavailable and Voice is not enabled for this organization. This patient will not receive survey notifications.';
            setPhoneTypeWarning(msg);
            toast(msg, { icon: '⚠️', duration: 8000 });
          } else {
            const msg = 'This phone number is a landline and cannot receive text messages. Contact method has been switched to Voice.';
            setPhoneTypeWarning(msg);
            toast(msg, { icon: '⚠️', duration: 6000 });
          }
        }
        setError(null);
        setFieldErrors({});
        setActiveStep(1);
      } catch (err: any) {
        // Check if this is a "patient already exists" error
        const errorMessage = err?.message || err?.errorData?.error || '';
        if (errorMessage.toLowerCase().includes('already exists')) {
          // Store the patient data and show confirmation dialog
          setPendingPatientData({ ...patientData });
          setDuplicateDialogMessage(`Patient with MRN "${patientData.patient_id}" already exists. Do you want to update the existing patient with the new information?`);
          setShowDuplicateDialog(true);
        } else {
          setError(err.message || 'Failed to create patient');
        }
      }
      setLoading(false);
    } else if (activeStep === 1) {
      // Continuation flow: documents already processed, no new inputs
      if (documentsProcessed && problemListFiles.length === 0 && medicationListFiles.length === 0
          && pastedProblemImages.length === 0 && pastedMedicationImages.length === 0
          && !problemListText.trim() && !medicationListText.trim()) {
        setActiveStep(2);
        return;
      }

      // Upload documents and parse them
      const nextErrors = getDocumentUploadErrors();
      setDocumentUploadErrors(nextErrors);
      if (nextErrors.problemList || nextErrors.medicationList) {
        setError('Please provide a Problem List (upload a file/image or enter text) before processing documents.');
        return;
      }

      // Skip re-processing if documents were already processed and inputs haven't changed
      const currentSnapshot = getInputSnapshot();
      if (documentsProcessed && processedInputSnapshot === currentSnapshot) {
        // Only re-upload vitals if they changed since last upload
        const currentVitals = getVitalsSnapshot();
        if (!vitalsUploaded || uploadedVitalsSnapshot !== currentVitals) {
          setLoading(true);
          await uploadVitalsOnly();
          setLoading(false);
        }
        setActiveStep(2);
      } else {
        // Inputs changed or first time — full processing
        await uploadAndParseDocuments();
      }
    } else if (activeStep === 2) {
      // This step is handled by ApprovalWorkflow component's onApprove callback
      // No action needed here as approveExtractedData is called directly
      return;
    } else if (activeStep === 3) {
      // This step is handled by CarePlanApproval component's onApprove callback
      // which generates the care plan if needed and then approves it
      return;
    } else if (activeStep === 4) {
      // Complete step - handled by SurveyResultsDisplay component
      return;
    }
  };

  const handleBack = () => {
    if (activeStep === 2 && ehrImported) {
      setActiveStep(0);
    } else {
      setActiveStep(prev => Math.max(0, prev - 1));
    }
    setError(null);
    // PHA-1079: stepping back is how a clinician starts the attempt over, so the failed attempt's
    // inline red boxes have to go with the banner. Clearing only `error` left "Failed to extract
    // problems / medications" pinned under the two fields, and neither is reachable from step 0 —
    // returning to Upload showed a screen that still looked broken before anything was retried.
    // Both states are enumerated: `parseErrors` (what the parse returned) and
    // `documentUploadErrors` (the required-field check), which render as separate red boxes on the
    // same two fields and would otherwise leave half the staleness behind.
    setParseErrors({});
    setDocumentUploadErrors({});
  };

  const handleProblemListUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles: UploadedFile[] = Array.from(files).map(file => ({
        file,
        name: file.name,
        size: file.size
      }));
      setProblemListFiles(prev => [...prev, ...newFiles]);
      setParseErrors(prev => ({ ...prev, problemList: undefined }));
    }
  };

  const handleMedicationListUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles: UploadedFile[] = Array.from(files).map(file => ({
        file,
        name: file.name,
        size: file.size
      }));
      setMedicationListFiles(prev => [...prev, ...newFiles]);
      setParseErrors(prev => ({ ...prev, medicationList: undefined }));
    }
  };

  const removeProblemFile = (index: number) => {
    setProblemListFiles(prev => prev.filter((_, i) => i !== index));
    setParseErrors(prev => ({ ...prev, problemList: undefined }));
    setError(null);
  };

  const removeMedicationFile = (index: number) => {
    setMedicationListFiles(prev => prev.filter((_, i) => i !== index));
    setParseErrors(prev => ({ ...prev, medicationList: undefined }));
    setError(null);
  };

  const removePastedProblemImage = (index: number) => {
    setPastedProblemImages(prev => prev.filter((_, i) => i !== index));
    setParseErrors(prev => ({ ...prev, problemList: undefined }));
    setError(null);
  };

  const removePastedMedicationImage = (index: number) => {
    setPastedMedicationImages(prev => prev.filter((_, i) => i !== index));
    setParseErrors(prev => ({ ...prev, medicationList: undefined }));
    setError(null);
  };

  // Handle paste events for images
  const handlePaste = async (event: React.ClipboardEvent<HTMLTextAreaElement>, type: 'problem' | 'medication') => {
    const items = event.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      // Check if the item is an image
      if (item.type.indexOf('image') !== -1) {
        event.preventDefault(); // Prevent default paste behavior for images

        const blob = item.getAsFile();
        if (!blob) continue;

        // Create a unique filename with timestamp
        const timestamp = new Date().getTime();
        const extension = blob.type.split('/')[1] || 'png';
        const fileName = `pasted-image-${timestamp}.${extension}`;

        // Create a File object from the blob
        const file = new File([blob], fileName, { type: blob.type });

        const uploadedFile: UploadedFile = {
          file,
          name: fileName,
          size: file.size
        };

        // Add to the appropriate state
        if (type === 'problem') {
          setPastedProblemImages(prev => [...prev, uploadedFile]);
          setParseErrors(prev => ({ ...prev, problemList: undefined }));
          toast.success(`Image pasted: ${fileName}`);
        } else {
          setPastedMedicationImages(prev => [...prev, uploadedFile]);
          setParseErrors(prev => ({ ...prev, medicationList: undefined }));
          toast.success(`Image pasted: ${fileName}`);
        }
      }
    }
  };

  // helper: turn File -> { bytes?: number[], content?: string, mime_type: string }
  async function fileToPayload(file: File): Promise<
    | { bytes: number[]; mime_type: string }
    | { content: string; mime_type: string }
  > {
    const mime = file.type || 'application/octet-stream';
    // treat as text only when it's clearly text
    const isText = /^text\/|\/(json|xml|csv)$/.test(mime);

    if (isText) {
      const text = await file.text();
      return { content: text, mime_type: mime };
    }

    // binary: read as ArrayBuffer and send raw bytes[]
    const buf = await file.arrayBuffer();
    const u8 = new Uint8Array(buf);
    return { bytes: Array.from(u8), mime_type: mime || 'application/octet-stream' };
  }


  // ADD THIS: base64 helper, replacing readFileAsText
  const readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result as string // "data:<mime>;base64,AAAA..."
        const base64 = result.split(',')[1] || ''
        resolve(base64)
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })

  const parseTextContent = async (text: string, documentType: 'problem_list' | 'medication_list') => {
    const body = {
      patient_id: patientData.patient_id,
      document_type: documentType,
      attributed_ts: new Date().toISOString(),
      content: text
      // Note: Do not include mime_type for plain text content
      // The server will handle it as text automatically
    };

    const response = await apiRequest('/session/patient/raw_document/parse', {
      method: 'POST',
      body: JSON.stringify(body),
    });

    const data = await response.json();
    return data.extracted_items || [];
  };

  // Upload vital signs only (extracted from uploadAndParseDocuments for reuse on back-navigation)
  const uploadVitalsOnly = async () => {
    const hasVitalSigns =
      vitalSigns.blood_pressure_systolic ||
      vitalSigns.blood_pressure_diastolic ||
      (vitalSigns.input_mode === 'height_weight' && (vitalSigns.height_feet || vitalSigns.height_inches || vitalSigns.weight_pounds)) ||
      (vitalSigns.input_mode === 'bmi_only' && vitalSigns.BMI);

    if (!hasVitalSigns) return;

    // Validate blood pressure values before submission
    if (vitalSigns.blood_pressure_systolic && !isValidSystolic(vitalSigns.blood_pressure_systolic)) {
      toast.error('Systolic blood pressure must be between 60-300 mmHg');
      return;
    }
    if (vitalSigns.blood_pressure_diastolic && !isValidDiastolic(vitalSigns.blood_pressure_diastolic)) {
      toast.error('Diastolic blood pressure must be between 40-200 mmHg');
      return;
    }

    try {
      const vitalSignsPayload: any = {
        patient_id: patientData.patient_id,
        attributed_ts: new Date().toISOString()
      };

      if (vitalSigns.blood_pressure_systolic) {
        vitalSignsPayload.blood_pressure_systolic = parseInt(vitalSigns.blood_pressure_systolic);
      }
      if (vitalSigns.blood_pressure_diastolic) {
        vitalSignsPayload.blood_pressure_diastolic = parseInt(vitalSigns.blood_pressure_diastolic);
      }

      if (vitalSigns.input_mode === 'height_weight') {
        const feet = parseInt(vitalSigns.height_feet);
        const inches = parseFloat(vitalSigns.height_inches);
        const weightLbs = parseFloat(vitalSigns.weight_pounds);

        if (!isNaN(feet) && !isNaN(inches)) {
          const heightCm = feetInchesToCentimeters(feet, inches);
          vitalSignsPayload.height = heightCm;
        }

        if (!isNaN(weightLbs)) {
          const weightKg = poundsToKilograms(weightLbs);
          vitalSignsPayload.weight = weightKg;
        }

        if (vitalSigns.BMI) {
          vitalSignsPayload.BMI = parseFloat(vitalSigns.BMI);
        }
      } else if (vitalSigns.input_mode === 'bmi_only' && vitalSigns.BMI) {
        vitalSignsPayload.BMI = parseFloat(vitalSigns.BMI);
      }

      const response = await apiRequest('/session/patient/items/upload_structured', {
        method: 'POST',
        body: JSON.stringify(vitalSignsPayload),
      });

      if (response.ok) {
        toast.success('Vital signs uploaded successfully');
      } else {
        const errorData = await response.json();
        console.error('Failed to upload vital signs:', errorData);
        toast.error('Failed to upload vital signs, but continuing...');
      }

      setVitalsUploaded(true);
      setUploadedVitalsSnapshot(getVitalsSnapshot());
    } catch (err) {
      console.error('Error uploading vital signs:', err);
      toast.error('Failed to upload vital signs, but continuing...');
    }
  };

  const uploadAndParseDocuments = async () => {
    const nextErrors = getDocumentUploadErrors();
    setDocumentUploadErrors(nextErrors);
    if (nextErrors.problemList || nextErrors.medicationList) {
      setError('Please provide a Problem List (upload a file/image or enter text) before processing documents.');
      return;
    }

    setLoading(true);
    setError(null);
    setParseErrors({});

    try {
      let localParseErrors: { problemList?: string; medicationList?: string } = {};
      const allExtractedProblems: ParsedProblem[] = [];
      const allExtractedMedications: ParsedMedication[] = [];

      // Combine all files (uploaded and pasted) for progress tracking
      const allFiles = [
        ...problemListFiles,
        ...medicationListFiles,
        ...pastedProblemImages,
        ...pastedMedicationImages
      ];

      // Build list of text parsing tasks
      const textTasks: Array<{ name: string; type: 'problem_list' | 'medication_list' }> = [];
      if (problemListText.trim()) {
        textTasks.push({ name: 'Problem List (Text)', type: 'problem_list' });
      }
      if (medicationListText.trim()) {
        textTasks.push({ name: 'Medication List (Text)', type: 'medication_list' });
      }

      // Initialize progress tracking if there are files or text to process
      if (allFiles.length > 0 || textTasks.length > 0) {
        const totalItems = allFiles.length + textTasks.length;
        const fileProgress = [
          ...allFiles.map(file => ({
            name: file.file.name,
            status: 'pending' as const,
            progress: 0
          })),
          ...textTasks.map(task => ({
            name: task.name,
            status: 'pending' as const,
            progress: 0
          }))
        ];

        setUploadProgress({
          files: fileProgress,
          overallProgress: 0,
          currentFile: allFiles.length > 0 ? allFiles[0]?.file.name : textTasks[0]?.name
        });
      }

      let completedItems = 0;
      const totalItems = allFiles.length + textTasks.length;

      // PHA-1079: why each section's files failed, if they did. Not an error state of its own —
      // it only supplies wording to the zero-extraction branch below, so a file that fails
      // alongside input that parses fine still reaches Review exactly as before.
      let fileParseErrors: { problemList?: string; medicationList?: string } = {};

      if (allFiles.length > 0) {
        // Process files with progress tracking
        const fileResults = await processDocumentsWithProgress(allFiles);

        // Get the extracted data from file processing
        allExtractedProblems.push(...fileResults.problems);
        allExtractedMedications.push(...fileResults.medications);
        fileParseErrors = fileResults.errors;
        completedItems += allFiles.length;
      }

      // Process problem list text if provided
      if (problemListText.trim()) {
        try {
          setUploadProgress(prev => ({
            ...prev,
            currentFile: 'Problem List (Text)',
            files: prev.files.map(f =>
              f.name === 'Problem List (Text)' ? { ...f, status: 'uploading' as const, progress: 50 } : f
            )
          }));

          const problemItems = await parseTextContent(problemListText.trim(), 'problem_list');
          allExtractedProblems.push(...problemItems);
          completedItems++;

          setUploadProgress(prev => ({
            ...prev,
            files: prev.files.map(f =>
              f.name === 'Problem List (Text)' ? { ...f, status: 'completed' as const, progress: 100 } : f
            ),
            overallProgress: (completedItems / totalItems) * 100
          }));

          toast.success('Problem list text parsed successfully');
        } catch (err) {
          console.error('Error parsing problem list text:', err);
          const errorMessage = err instanceof Error ? err.message : 'Failed to extract problems from text';
          localParseErrors.problemList = errorMessage;
          setParseErrors(prev => ({ ...prev, problemList: errorMessage }));

          setUploadProgress(prev => ({
            ...prev,
            files: prev.files.map(f =>
              f.name === 'Problem List (Text)' ? {
                ...f,
                status: 'error' as const,
                progress: 0,
                error: err instanceof Error ? err.message : 'Parsing failed'
              } : f
            )
          }));
          toast.error('Failed to parse problem list text');
        }
      }

      // Process medication list text if provided
      if (medicationListText.trim()) {
        try {
          setUploadProgress(prev => ({
            ...prev,
            currentFile: 'Medication List (Text)',
            files: prev.files.map(f =>
              f.name === 'Medication List (Text)' ? { ...f, status: 'uploading' as const, progress: 50 } : f
            )
          }));

          const medicationItems = await parseTextContent(medicationListText.trim(), 'medication_list');
          allExtractedMedications.push(...medicationItems);
          completedItems++;

          setUploadProgress(prev => ({
            ...prev,
            files: prev.files.map(f =>
              f.name === 'Medication List (Text)' ? { ...f, status: 'completed' as const, progress: 100 } : f
            ),
            overallProgress: (completedItems / totalItems) * 100
          }));

          toast.success('Medication list text parsed successfully');
        } catch (err) {
          console.error('Error parsing medication list text:', err);
          const errorMessage = err instanceof Error ? err.message : 'Failed to extract medications from text';
          localParseErrors.medicationList = errorMessage;
          setParseErrors(prev => ({ ...prev, medicationList: errorMessage }));

          setUploadProgress(prev => ({
            ...prev,
            files: prev.files.map(f =>
              f.name === 'Medication List (Text)' ? {
                ...f,
                status: 'error' as const,
                progress: 0,
                error: err instanceof Error ? err.message : 'Parsing failed'
              } : f
            )
          }));
          toast.error('Failed to parse medication list text');
        }
      }

      // Wait a bit before proceeding
      if (allFiles.length > 0 || textTasks.length > 0) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      // Check if we have any data at all
      if (allExtractedProblems.length === 0 && allExtractedMedications.length === 0) {
        // In continuation flow, skip error if we have existing data from previous upload
        if (!documentsProcessed || (extractedData.problems.length === 0 && extractedData.medications.length === 0)) {
          localParseErrors = resolveNoExtractionErrors({
            ...getInputPresence(),
            // A text-parse failure is more specific than a file one, so it wins where both
            // happened in the same section.
            existing: { ...fileParseErrors, ...localParseErrors },
          });
          setParseErrors(localParseErrors);
          toast.error(NO_EXTRACTION_ERROR);
        }
      }

      // Upload vital signs
      await uploadVitalsOnly();

      // Check if there were any parse errors
      if (localParseErrors.problemList || localParseErrors.medicationList) {
        setError('Some documents failed to parse. Please review the errors below and try again.');
        setLoading(false);
        return;
      }

      // Update extracted data — merge with existing for partial re-upload
      // PHA-1045/PHA-1046: in the REFRESH flow the newly uploaded lists are diffed against
      // the last approved plan instead of coming in flat, so the Review step can mark each
      // item kept / rejected / new. `refreshBaseline` is only set by the refresh entry, so
      // first-time onboarding falls through to the original behaviour untouched.
      setExtractedData(prev => ({
        problems: allExtractedProblems.length > 0
          ? enrichProblemsWithICD10(
              refreshBaseline
                ? buildRefreshReviewList({
                    approved: refreshBaseline.approvedProblems,
                    priorCandidates: refreshBaseline.priorProblemCandidates,
                    sourced: allExtractedProblems,
                    keyField: 'problem',
                  })
                : allExtractedProblems.map(p => ({ ...p, approved: true }))
            )
          : prev.problems,
        medications: allExtractedMedications.length > 0
          ? (refreshBaseline
              ? buildRefreshReviewList({
                  approved: refreshBaseline.approvedMedications,
                  priorCandidates: refreshBaseline.priorMedicationCandidates,
                  sourced: allExtractedMedications,
                  keyField: 'medication',
                })
              : allExtractedMedications.map(m => ({ ...m, approved: true })))
          : prev.medications
      }));

      // Mark documents as processed so back-navigation can skip re-processing
      setDocumentsProcessed(true);
      setProcessedInputSnapshot(getInputSnapshot());

      setActiveStep(2);
    } catch (err: any) {
      setError(err.message || 'Failed to upload and parse documents');
    }
    setLoading(false);
  };

  // PHA-1079: which sections the clinician actually gave something to parse. Shared with the
  // "no items extracted" branch of uploadAndParseDocuments so an error is never attached to a
  // section that was left empty on purpose.
  const getInputPresence = () => ({
    hasProblemListInput:
      problemListFiles.length > 0 ||
      pastedProblemImages.length > 0 ||
      problemListText.trim().length > 0,
    hasMedicationListInput:
      medicationListFiles.length > 0 ||
      pastedMedicationImages.length > 0 ||
      medicationListText.trim().length > 0,
  });

  const getDocumentUploadErrors = () => {
    const { hasProblemListInput, hasMedicationListInput } = getInputPresence();

    // During continuation (documentsProcessed), allow partial re-upload
    if (documentsProcessed && (hasProblemListInput || hasMedicationListInput)) {
      return { problemList: undefined, medicationList: undefined } as {
        problemList?: string;
        medicationList?: string;
      };
    }

    return {
      problemList: hasProblemListInput
        ? undefined
        : 'Required: upload a problem list file/image or enter problem list text.',
      // PHA-1030: only the problem list is required. Patients with no medications on
      // file have nothing to upload here, and blocking the step stranded them outside
      // the system entirely.
      medicationList: undefined,
    } as {
      problemList?: string;
      medicationList?: string;
    };
  };

  // PHA-1072 (second report): is anything staged on the Document Upload step right now? Shared
  // by the step's banner and its forward-button label so the two can never disagree about it.
  const hasStagedDocumentInput =
    problemListFiles.length > 0 || medicationListFiles.length > 0
    || pastedProblemImages.length > 0 || pastedMedicationImages.length > 0
    || problemListText.trim() !== '' || medicationListText.trim() !== '';

  const uploadStepPrompt = getUploadStepPrompt({
    refreshSource,
    documentsProcessed,
    hasStagedInput: hasStagedDocumentInput,
  });

  const canProcessDocuments = (() => {
    if (documentsProcessed && problemListFiles.length === 0 && medicationListFiles.length === 0
        && pastedProblemImages.length === 0 && pastedMedicationImages.length === 0
        && !problemListText.trim() && !medicationListText.trim()) {
      return true;
    }
    const errs = getDocumentUploadErrors();
    return !errs.problemList && !errs.medicationList;
  })();



  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  const approveExtractedData = async () => {
    setLoading(true);
    setError(null);

    try {
      // PHA-1141: every NEW item from an EHR-sourced regeneration must be decided. The button is
      // disabled for this in ApprovalWorkflow; this is the backstop, because an undecided item
      // that reached update_patient_data_approval would land reviewed=false — which PHA-1029
      // reads as "not reviewed yet, accept by default" the next time.
      const undecided = [...extractedData.problems, ...extractedData.medications].filter(isUndecidedNewItem);
      if (undecided.length > 0) {
        setError(`Accept or reject each item marked NEW before continuing (${undecided.length} left).`);
        setLoading(false);
        return;
      }

      // Approve problems
      const approvedProblems = extractedData.problems.filter(p => p.approved);

      // Validate that at least one problem is approved
      if (approvedProblems.length === 0) {
        setError('Cannot generate care plan: At least one problem must be approved. Care plans require medical problems to be defined.');
        setLoading(false);
        return;
      }

      // PHA-1030: medications are optional. There used to be a second gate here demanding an
      // approved medication (PHA-1067 had hoisted it above both writes so a medication-less
      // review could not persist one list and not the other). Patients with nothing on file
      // in the EHR could not clear it and so could not be onboarded at all. Both lists are
      // still written together below, empty or not — persist/stage both no-op on an empty
      // batch — so PHA-1067's paired-write invariant is preserved.

      // Strip frontend-only fields before sending to backend
      // PHA-1046: priorState joins the list — it is a refresh-flow display marker and must
      // not reach the backend.
      const cleanItem = (item: any) => {
        const { isNew, isModified, originalValue, icd10_needs_review, priorState, ...clean } = item;
        return clean;
      };

      // PHA-1067: a REGENERATION stages its review; only a first-time onboarding commits here.
      //
      // approveItems writes straight through to the patient's live problem/medication lists —
      // update_patient_data_approval deletes and re-inserts the candidate rows the
      // problem_list / medication_list views read. Doing that on the way to the care-plan step
      // published the clinician's accept/reject decisions before any plan existed, and
      // destroyed the batch that could have been restored: abandoning the regeneration (which
      // makes no server call at all) or cancelling it both left the rejections in force.
      // Staged decisions are promoted by the care-plan approval and dropped by Cancel.
      //
      // First-time onboarding is deliberately untouched: it has no previous valid list to
      // protect, and PHA-1033 requires the creation workflow not change.
      //
      // PHA-1067 follow-up: staging is what makes the decisions promotable at approval, and
      // promotion is scoped to the draft batch the staged review generated. Re-staging resets
      // that link — correct at the START of a regeneration (nothing generated yet), and silent
      // data loss on a RESUMED one: the clinician re-enters Review on a saved draft, presses
      // Continue, approves the draft that is already on screen, and the promotion finds no
      // matching batch. `reviewChangedSinceGeneration` only raises an amber "consider
      // regenerating" callout, so nothing forces a regenerate in between. Hand the batch on
      // screen to the stage call so the link survives. Empty for a fresh regeneration —
      // enterRefreshReview clears carePlanData — which keeps the original reset behaviour.
      const draftBatchTimestamps = Array.from(
        new Set(
          ((carePlanData?.care_plan_definitions || []) as any[])
            .map((plan) => plan?.uploaded_ts)
            .filter(Boolean)
            .map(String)
        )
      );
      const persistItems = refreshSource
        ? (patientId: string, documentType: 'problem_list' | 'medication_list', items: any[]) =>
            patientApi.stageItems(patientId, documentType, items, draftBatchTimestamps)
        : patientApi.approveItems;

      await persistItems(patientData.patient_id, 'problem_list', extractedData.problems.map(cleanItem));
      await persistItems(patientData.patient_id, 'medication_list', extractedData.medications.map(cleanItem));

      // Move directly to Care Plan Approval (which will generate if needed)
      setActiveStep(3);
    } catch (err: any) {
      setError(err.message || 'Failed to approve extracted data');
    }
    setLoading(false);
  };

  // PHA-1157: `options.resetToGuidelines` regenerates from the standard guidelines only,
  // instead of starting from the previous approved plan's edits.
  const generateCarePlan = async (selectedDiseases?: string[], options?: { resetToGuidelines?: boolean }) => {
    setCarePlanLoading(true);
    setError(null);

    try {
      // Validate that problems were approved before attempting to generate
      const approvedProblems = extractedData.problems.filter(p => p.approved);
      if (approvedProblems.length === 0) {
        const errorMsg = 'Cannot generate care plan: No approved problems found. At least one medical problem must be approved to generate a care plan.';
        setError(errorMsg);
        throw new Error(errorMsg);
      }

      // PHA-810: a transient blip on the generate POST used to reject, which
      // tore down the progress card (carePlanLoading→false), flashed a red error,
      // then an effect re-fired generation — so the bar visibly reset from ~70%
      // back to 0% before eventually succeeding. Retry inline here instead, WITHOUT
      // flipping carePlanLoading, so the bar keeps climbing in one smooth pass and
      // no error flashes; only a fully-exhausted run surfaces an error.
      const GENERATE_MAX_ATTEMPTS = 2;
      let response: any;
      for (let attempt = 1; ; attempt++) {
        try {
          response = await patientApi.generateCarePlan(
            patientData.patient_id,
            selectedDiseases,
            // PHA-1067: a regeneration's review lives in staging, not on the patient's live
            // lists, so the generator has to be told to read it. False for a first-time
            // onboarding, which commits its review the old way.
            !!refreshSource,
            !!options?.resetToGuidelines,
          );
          break;
        } catch (attemptErr: any) {
          if (attempt >= GENERATE_MAX_ATTEMPTS) throw attemptErr;
          // brief backoff, then retry while the progress bar keeps running
          await new Promise((r) => setTimeout(r, 1000 * attempt));
        }
      }
      setCarePlanData(response);
      setCarePlanGenerated(true);

      // PHA-815: cadence comes from each question's backend computed_cadence; no
      // client-side org-template fetch / re-derivation needed.
      setCarePlanDirtySinceGeneration(false);
      persistCarePlanDirty(false);
      return response;
    } catch (err: any) {
      // Check if error is related to missing problems/items
      const errorMessage = err.message || 'Failed to generate care plan';
      if (errorMessage.includes('no problem') || errorMessage.includes('no approved') || errorMessage.includes('requires problems')) {
        setError('Cannot generate care plan: No approved problems available. At least one medical problem must be approved to generate a care plan.');
      } else {
        setError(`Oops, something went wrong with creating the care plan (${errorMessage})`);
      }
      throw err;
    } finally {
      setCarePlanLoading(false);
    }
  };

  const editCarePlan = () => {
    // This functionality will be integrated into CarePlanDisplay component
    console.log('Edit care plan functionality can be added to CarePlanDisplay');
  };

  const saveCarePlan = () => {
    // This functionality will be integrated into CarePlanDisplay component
    console.log('Save care plan functionality can be added to CarePlanDisplay');
  };

  // PHA-1064: `approveCarePlan` used to live here — `() => setActiveStep(4)`, unreferenced.
  // It is deleted rather than left dormant because it reproduces the exact bug this ticket is
  // about: landing on step 4 without setting surveyResultsGenerated renders the
  // "Generating Survey Questions" spinner forever. handleCarePlanApproval below is the only
  // legitimate way to reach step 4, and it sets the flag in the same batch.

  // PHA-967: tear the wizard down and hand the Dashboard the patient to open. Used by the
  // Save-as-Draft exit only — Approve ends on the "Complete" step (activeStep 4), whose Done
  // button resets to the patient list. The identity is captured BEFORE resetWorkflow, which
  // blanks patientData. Falls back to the plain reset (→ patient list) when no host handler is
  // wired, so the component still works standalone.
  const exitToPatientChart = () => {
    const patientId = patientData.patient_id?.trim();
    if (!onExitToPatientChart || !patientId) {
      resetWorkflow();
      return;
    }
    const identity = {
      patientId,
      firstName: patientData.first_name?.trim() || undefined,
      lastName: patientData.last_name?.trim() || undefined,
      mrn: patientId,
    };
    resetWorkflow({ navigateHome: false });
    onExitToPatientChart(identity);
  };

  // PHA-967: "Save as Draft" from the care-plan step. The draft is already persisted by
  // CarePlanApproval before this fires; all that is left is to leave the wizard for the chart.
  // NOTE: this deliberately does NOT call /session/patient/onboard/complete — no consent is
  // recorded and no surveys are generated (PHA-895). The patient still has no approved care plan,
  // so the chart header keeps showing "Continue Onboarding" to get back in.
  const handleSaveDraftAndExit = () => {
    exitToPatientChart();
  };

  // PHA-1049: "Cancel Care Plan Regeneration". Refresh flow only — during a first-time
  // onboarding there is no previous plan to fall back to, so discarding would leave the
  // patient with nothing and the button is not rendered (see the prop gate below).
  const handleCancelRegeneration = async () => {
    const patientId = patientData.patient_id?.trim();
    if (!patientId) return;
    // PHA-1050: scope the discard to the batch on screen. Without this the server bins
    // every in-flight manual draft for the patient, which is only safe while Cancel is
    // unreachable for a patient with no approved plan to fall back on.
    const batchTimestamps = Array.from(
      new Set(
        ((carePlanData?.care_plan_definitions || []) as any[])
          .map((plan) => plan?.uploaded_ts)
          .filter(Boolean)
          .map(String)
      )
    );
    try {
      await patientApi.discardCarePlanDraft(patientId, batchTimestamps);
    } catch (e: any) {
      // Do NOT navigate away on failure: telling the clinician the regeneration was
      // cancelled while the draft is still on the chart is the worse outcome.
      console.error('Failed to discard the regeneration draft:', e);
      toast.error('Could not discard the regeneration. Please try again.');
      return;
    }
    // The server-side draft is gone; clear the client-side remnants too, or the next entry
    // restores the autosave snapshot of what was just discarded.
    localStorage.removeItem(`carePlanDirty_${patientId}`);
    setCarePlanData(null);
    setCarePlanGenerated(false);
    setCarePlanDirtySinceGeneration(false);
    setRefreshSource(null);
    setRefreshBaseline(null);
    toast.success('Care plan regeneration cancelled. The current care plan is unchanged.');
    exitToPatientChart();
  };

  const handleCarePlanApproval = async (surveys: any[], consentGiven: boolean) => {
    // PHA-895: consent is now recorded AND surveys generated inside CarePlanApproval via
    // /session/patient/onboard/complete (deferred survey generation). By the time this fires,
    // onboarding is already finalized and `surveys` carries the freshly-generated + linked rows.
    setSurveyResults(surveys);
    setSurveyResultsGenerated(true);
    setPatientConsentGiven(consentGiven);
    setActiveStep(4);
  };

  const handleConsentDeclined = async () => {
    setLoading(true);
    try {
      await apiRequest('/session/patient/onboard/complete', {
        method: 'POST',
        body: JSON.stringify({
          patient_id: patientData.patient_id,
          consent_status: 'denied',
        }),
      });
      toast.success('Patient has declined consent.');
    } catch (err) {
      console.error('Failed to save consent:', err);
    } finally {
      setLoading(false);
    }
    resetWorkflow();
  };

  const generateQuestions = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiRequest('/session/patient/generate_questions', {
        method: 'POST',
        body: JSON.stringify({
          patient_id: patientData.patient_id
        })
      });

      const data = await response.json();
      setQuestions(data.questions);
      setVoiceScripts(data.voice_scripts);
      setQuestionsGenerated(true);
    } catch (err: any) {
      setError(err.message || 'Failed to generate questions');
    }
    setLoading(false);
  };

  // Handle user's response to duplicate patient dialog
  const handleDuplicateDialogClose = (shouldUpdate: boolean) => {
    setShowDuplicateDialog(false);

    // EHR branch: the details to write come from the EHR, not from the form, so re-run the
    // import with the flag that lets it land on an existing MRN. Declining leaves the
    // patient untouched — no partial write happened, the backend refused before writing.
    if (pendingEhrImportMrn) {
      setPendingEhrImportMrn(null);
      setDuplicateDialogMessage('');
      if (shouldUpdate) {
        handleImportFromEhr({ updateIfExists: true });
      }
      return;
    }

    if (shouldUpdate && pendingPatientData) {
      handleUpdateExistingPatient();
    } else {
      // User chose "No" - just close the dialog and clear pending data
      setPendingPatientData(null);
      setDuplicateDialogMessage('');
    }
  };

  // Update existing patient with new data (only non-empty fields)
  const handleUpdateExistingPatient = async () => {
    if (!pendingPatientData) return;

    setLoading(true);
    setError(null);

    try {
      console.log('🔄 Updating existing patient:', pendingPatientData);

      // Build update payload with only non-empty fields
      const updatePayload: any = {
        patient_id: pendingPatientData.patient_id,
      };

      if (pendingPatientData.first_name?.trim()) {
        updatePayload.first_name = pendingPatientData.first_name.trim();
      }
      if (pendingPatientData.last_name?.trim()) {
        updatePayload.last_name = pendingPatientData.last_name.trim();
      }
      if (pendingPatientData.date_of_birth?.trim()) {
        const datePattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
        const dateMatch = pendingPatientData.date_of_birth.trim().match(datePattern);
        if (dateMatch && isValidDate(pendingPatientData.date_of_birth.trim())) {
          const [, month, day, year] = dateMatch;
          updatePayload.date_of_birth = `${year}-${month}-${day}`;
        } else {
          updatePayload.date_of_birth = pendingPatientData.date_of_birth.trim();
        }
      }
      if (pendingPatientData.phone_number?.trim()) {
        updatePayload.phone_number = pendingPatientData.phone_number.trim().replace(/[\s\-\(\)]/g, '');
      }
      if (pendingPatientData.gender?.trim()) {
        updatePayload.gender = pendingPatientData.gender.trim();
      }

      await patientApi.update(updatePayload);
      console.log('✅ Patient updated successfully');

      // PHA-1117: this branch reaches Step 2 without loadExistingPatientData ever running, so
      // it needs the same clinic pin as the create path — the patient exists, and the wizard
      // must resolve the review cycle and org flags against THEIR clinic, not against nothing.
      await syncPatientOrgIdFromRecord(pendingPatientData.patient_id);

      toast.success(`Patient ${pendingPatientData.first_name} ${pendingPatientData.last_name} (${pendingPatientData.patient_id}) has been updated!`);

      // Continue to next step
      setError(null);
      setFieldErrors({});
      setActiveStep(1);

    } catch (updateError: any) {
      console.error('❌ Error updating patient:', updateError);

      // Check if the error is about patient not being linked to user
      const updateErrorMessage = updateError?.message || updateError?.errorData?.error || '';
      if (updateErrorMessage.toLowerCase().includes('not linked') ||
        updateErrorMessage.toLowerCase().includes('not associated')) {
        setError(`Cannot update: This patient exists but is not linked to your account. Please contact an administrator to link this patient to you first.`);
      } else {
        setError(updateError.message || 'Failed to update patient. Please try again.');
      }
    }

    setPendingPatientData(null);
    setDuplicateDialogMessage('');
    setLoading(false);
  };

  // PHA-967: `navigateHome` is opt-out so the exit-to-chart path can leave navigation to the
  // Dashboard. Two navigators racing (navigate('/') here vs. the tab switch there) is exactly the
  // kind of ordering dependency that breaks silently — there is one authority per exit path.
  const resetWorkflow = ({ navigateHome = true }: { navigateHome?: boolean } = {}) => {
    setActiveStep(0);
    setPatientData({
      patient_id: '',
      first_name: '',
      last_name: '',
      date_of_birth: '',
      phone_number: '',
      gender: '',
      preferred_contact_method: 'sms',
      language: 'en-US',
    });
    setProblemListFiles([]);
    setMedicationListFiles([]);
    setProblemListText('');
    setMedicationListText('');
    setPastedProblemImages([]);
    setPastedMedicationImages([]);
    setExtractedData({ problems: [], medications: [] });
    setDocumentsProcessed(false);
    setProcessedInputSnapshot(null);
    setVitalsUploaded(false);
    setUploadedVitalsSnapshot(null);
    setCarePlanData(null);
    setCarePlanGenerated(false);
    setCarePlanDirtySinceGeneration(false);
    persistCarePlanDirty(false);
    setSurveyResults([]);
    setSurveyResultsGenerated(false);
    setQuestions([]);
    setVoiceScripts([]);
    setQuestionsGenerated(false);
    setLoadedConsentStatus(null);
    // PHA-1033: leaving the wizard ends the refresh, whichever exit was taken. Leaving these
    // set would make the NEXT entry — possibly a first-time onboarding for a different
    // patient — render the refresh-only affordances and diff against a stale baseline.
    setRefreshSource(null);
    setRefreshBaseline(null);
    setEhrRefreshBusy(false);
    setPatientOrgId(null);
    setError(null);
    setParseErrors({});

    // PHA-1065: everything below used to survive a reset, which is how "cannot create a new
    // patient — all fields grey" happened. `ehrImported` is the one that produced the report:
    // it is the flag that disables the MRN box, Generate, Import from EHR and every demographic
    // field once an import lands (intentionally). resetWorkflow blanked patientData but left
    // that flag TRUE, so the clinician got a blank Step-1 form with every control disabled and
    // no way to type an MRN — unrecoverable without a page reload. Reachable from any reset:
    // "Onboard Another Patient" (SurveyResultsDisplay onComplete), a declined consent, or an
    // exit-to-chart, none of which remount the permanently-mounted Onboarding tab (PHA-895).
    //
    // The rest is the same class of leak, enumerated rather than fixed one symptom at a time —
    // stale per-patient state bleeding into the next patient. `vitalSigns` is the one that
    // matters clinically: without this the previous patient's BP/height/weight stayed prefilled
    // on the new patient's form.
    //
    // Deliberately NOT reset: hasEhrIntegration, voiceEnabled, voiceOfPatientEnabled,
    // noTargetDatesEnabled, supportedLanguages — per-mount org config, not per-patient state,
    // and re-fetching them on every reset would flicker the form for no reason.
    setEhrImported(false);
    setEhrImportResult(null);
    setEhrImportLoading(false);
    setEhrPhoneRequired(false);
    setExistingPatientMatch(null);
    setPendingEhrImportMrn(null);
    setIsExistingPatient(false);
    setShowDuplicateDialog(false);
    setDuplicateDialogMessage('');
    setPendingPatientData(null);
    setFieldErrors({});
    setDocumentUploadErrors({});
    setUploadModalOpen(false);
    setUploadProgress({ files: [], overallProgress: 0 });
    setPreviousDocumentNames({});
    setPatientPdfMeta({});
    setPatientConsentGiven(false);
    setVitalSigns({
      blood_pressure_systolic: '',
      blood_pressure_diastolic: '',
      height_feet: '',
      height_inches: '',
      weight_pounds: '',
      BMI: '',
      input_mode: 'height_weight',
    });
    // A reset ends whatever was in flight; leaving these true disables the form's controls just
    // as surely as ehrImported did.
    setLoading(false);
    setCarePlanLoading(false);

    if (navigateHome) navigate('/');
  };

  return (
    <Box ref={containerRef} tabIndex={-1} className="p-6">
      {(() => {
        console.log('OnboardingWorkflow rendering with activeStep:', activeStep);
        console.log('OnboardingWorkflow current patient data:', patientData);
        console.log('OnboardingWorkflow loading state:', loading);
        return null;
      })()}
      <Heading size="6" className="text-slate-800 mb-6">
        Patient Onboarding
      </Heading>

      <Card className="p-6 mb-6">
        {/* Compact Progress Steps - fits all screen sizes */}
        <Box className="mb-6">
          <Flex align="start" justify="center" gap="1">
            {steps.map((label, index) => {
              // Abbreviated labels for compact display
              const shortLabels = [
                'Patient',
                'Upload',
                'Review',
                'Care Plan',
                'Complete'
              ];
              return (
                <React.Fragment key={label}>
                  {/* Step circle with label below */}
                  <Flex
                    direction="column"
                    align="center"
                    gap="1"
                    className="flex-shrink-0"
                    style={{ minWidth: '60px', maxWidth: '70px' }}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center border-2 text-xs font-semibold transition-all ${index < activeStep ? 'bg-blue-600 border-blue-600 text-white' :
                        index === activeStep ? 'border-blue-600 text-blue-600 bg-blue-50 ring-2 ring-blue-200' :
                          'border-slate-300 text-slate-400 bg-slate-50'
                      }`}>
                      {index < activeStep ? '✓' : index + 1}
                    </div>
                    {/* Show abbreviated label for all steps */}
                    <Text
                      size="1"
                      weight={index === activeStep ? "bold" : "medium"}
                      className={`text-center leading-tight ${index <= activeStep ? 'text-blue-600' : 'text-slate-400'
                        }`}
                      style={{ fontSize: '10px' }}
                    >
                      {shortLabels[index]}
                    </Text>
                  </Flex>

                  {/* Connecting line (except after last step) */}
                  {index < steps.length - 1 && (
                    <div
                      className={`h-0.5 flex-shrink-0 mt-3.5 ${index < activeStep ? 'bg-blue-600' : 'bg-slate-200'
                        }`}
                      style={{ width: '16px' }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </Flex>
        </Box>

        {error && (
          <Callout.Root color="red" className="mb-6">
            <Callout.Text>
              {error}
            </Callout.Text>
          </Callout.Root>
        )}

        {/* PHA-1044: the EHR refresh is asynchronous, so the refresh flow sits here polling
            for the new candidate batch. Give it a visible progress bar rather than an
            unexplained spinner — a structured FHIR pull is usually seconds, but a queued or
            already-running sync can take longer. */}
        {ehrRefreshBusy && (
          <Card className="p-6 mb-6">
            <Heading size="4" className="text-slate-800 mb-2">
              Importing the latest data from the EHR
            </Heading>
            <Text size="2" className="text-slate-600 block mb-4">
              Waiting for the refreshed problem list and medications. The current care plan
              is untouched until you approve a new one.
            </Text>
            <TimedProgress
              active={ehrRefreshBusy}
              estimatedDurationMs={EHR_REFRESH_POLL_INTERVAL_MS * 4}
              steps={EHR_IMPORT_PROGRESS_STEPS}
            />
          </Card>
        )}

        {/* Step 1: Patient Information */}
        {activeStep === 0 && (
          <Card className="p-6">
            <Heading size="5" className="text-slate-800 mb-4">
              Patient Information
            </Heading>
            <Flex direction="column" gap="4">
              <Flex gap="2" align="end">
                <Box className="flex-1">
                  <Text size="2" weight="medium" className="mb-2 block">Medical Record Number (MRN) *</Text>
                  <TextField.Root
                    size="3"
                    className="w-full"
                    style={fieldErrors.patient_id ? { borderColor: 'red', borderWidth: '2px' } : {}}
                  >
                    <TextField.Input
                      value={patientData.patient_id}
                      onChange={(e) => {
                        setPatientData(prev => ({ ...prev, patient_id: (e.target as HTMLInputElement).value }));
                        if (fieldErrors.patient_id) {
                          setFieldErrors(prev => ({ ...prev, patient_id: undefined }));
                        }
                        // PHA-958: the phone prompt belongs to one specific chart. Editing
                        // the MRN targets a different patient, whose EHR record may well
                        // have a phone — so drop the requirement rather than carry it over.
                        if (ehrPhoneRequired) {
                          setEhrPhoneRequired(false);
                          setFieldErrors(prev => ({ ...prev, phone_number: undefined }));
                        }
                      }}
                      placeholder="Enter MRN"
                      required
                      disabled={ehrImported}
                    />
                  </TextField.Root>
                  {fieldErrors.patient_id && (
                    <Text size="1" color="red" className="mt-1">{fieldErrors.patient_id}</Text>
                  )}
                </Box>
                <Flex gap="2">
                  <Button variant="outline" onClick={generatePatientId} size="3" disabled={ehrImported}>
                    Generate
                  </Button>
                  {hasEhrIntegration && (
                    <Button
                      variant="solid"
                      onClick={() => handleImportFromEhr()}
                      size="3"
                      disabled={ehrImported || ehrImportLoading || !patientData.patient_id?.trim()}
                    >
                      {ehrImportLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Importing...
                        </>
                      ) : ehrPhoneRequired ? (
                        'Finish Import'
                      ) : (
                        'Import from EHR'
                      )}
                    </Button>
                  )}
                </Flex>
              </Flex>

              {ehrImportLoading && (
                <Box className="mt-4 max-w-xl">
                  <TimedProgress
                    active={ehrImportLoading}
                    title="Importing from EHR"
                    estimatedDurationMs={8000}
                    steps={EHR_IMPORT_PROGRESS_STEPS}
                  />
                </Box>
              )}

              {/* PHA-397: identity confirmation banner on duplicate-MRN re-import */}
              {existingPatientMatch && (
                <Callout.Root color="blue" className="mt-2">
                  <Callout.Icon><CheckCircledIcon /></Callout.Icon>
                  <Callout.Text>
                    <Flex justify="between" align="center" gap="2">
                      <Box>
                        <Text weight="bold">Matched existing patient: </Text>
                        <Text>{existingPatientMatch.first_name} {existingPatientMatch.last_name}</Text>
                        <Text color="gray"> · DOB {existingPatientMatch.date_of_birth} · MRN {existingPatientMatch.mrn}</Text>
                        <br />
                        <Text size="1" color="gray">EHR refresh queued — care plan updates will appear on the patient's Care Plans tab within a few minutes.</Text>
                      </Box>
                      <Button
                        size="2"
                        variant="soft"
                        onClick={() => {
                          window.dispatchEvent(new CustomEvent('phanova:open-patient-detail', {
                            detail: { patientId: existingPatientMatch.patient_id, initialTab: 2 },
                          }));
                        }}
                      >
                        Open chart
                      </Button>
                    </Flex>
                  </Callout.Text>
                </Callout.Root>
              )}

              <Box>
                <Text size="2" weight="medium" className="mb-2 block">First Name *</Text>
                <TextField.Root
                  size="3"
                  className="w-full"
                  style={fieldErrors.first_name ? { borderColor: 'red', borderWidth: '2px' } : {}}
                >
                  <TextField.Input
                    value={patientData.first_name}
                    onChange={(e) => {
                      setPatientData(prev => ({ ...prev, first_name: (e.target as HTMLInputElement).value }));
                      if (fieldErrors.first_name) {
                        setFieldErrors(prev => ({ ...prev, first_name: undefined }));
                      }
                    }}
                    placeholder="Enter first name"
                    required
                    disabled={ehrImported}
                  />
                </TextField.Root>
                {fieldErrors.first_name && (
                  <Text size="1" color="red" className="mt-1">{fieldErrors.first_name}</Text>
                )}
              </Box>

              <Box>
                <Text size="2" weight="medium" className="mb-2 block">Last Name *</Text>
                <TextField.Root
                  size="3"
                  className="w-full"
                  style={fieldErrors.last_name ? { borderColor: 'red', borderWidth: '2px' } : {}}
                >
                  <TextField.Input
                    value={patientData.last_name}
                    onChange={(e) => {
                      setPatientData(prev => ({ ...prev, last_name: (e.target as HTMLInputElement).value }));
                      if (fieldErrors.last_name) {
                        setFieldErrors(prev => ({ ...prev, last_name: undefined }));
                      }
                    }}
                    placeholder="Enter last name"
                    required
                    disabled={ehrImported}
                  />
                </TextField.Root>
                {fieldErrors.last_name && (
                  <Text size="1" color="red" className="mt-1">{fieldErrors.last_name}</Text>
                )}
              </Box>

              <Box className="max-w-xs">
                <Text size="2" weight="medium" className="mb-2 block">Date of Birth *</Text>
                <TextField.Root
                  size="3"
                  className="w-full"
                  style={fieldErrors.date_of_birth ? { borderColor: 'red', borderWidth: '2px' } : {}}
                >
                  <TextField.Input
                    type="text"
                    value={patientData.date_of_birth}
                    onChange={(e) => {
                      const input = maskDateInput((e.target as HTMLInputElement).value);
                      setPatientData(prev => ({ ...prev, date_of_birth: input }));
                      if (fieldErrors.date_of_birth) {
                        setFieldErrors(prev => ({ ...prev, date_of_birth: undefined }));
                      }
                    }}
                    placeholder="MMDDYYYY"
                    maxLength={10}
                    required
                    disabled={ehrImported}
                  />
                </TextField.Root>
                {fieldErrors.date_of_birth ? (
                  <Text size="1" color="red" className="mt-1">{fieldErrors.date_of_birth}</Text>
                ) : (
                  <Text size="1" color="gray" className="mt-1">Enter MMDDYYYY (e.g., 01012000)</Text>
                )}
              </Box>

              <Box className="max-w-xs">
                <Text size="2" weight="medium" className="mb-2 block">Phone Number *</Text>
                <TextField.Root
                  size="3"
                  className="w-full"
                  style={fieldErrors.phone_number ? { borderColor: 'red', borderWidth: '2px' } : {}}
                >
                  <TextField.Input
                    type="tel"
                    value={patientData.phone_number}
                    onChange={(e) => {
                      setPatientData(prev => ({ ...prev, phone_number: (e.target as HTMLInputElement).value }));
                      if (fieldErrors.phone_number) {
                        setFieldErrors(prev => ({ ...prev, phone_number: undefined }));
                      }
                    }}
                    onBlur={(e) => {
                      const value = (e.target as HTMLInputElement).value.trim().replace(/[\s\-\(\)]/g, '');
                      if (value && !/^(\d{10}$)|(^\+[1-9]\d{1,14}$)/.test(value)) {
                        setFieldErrors(prev => ({
                          ...prev,
                          phone_number: 'Phone number must be 10 digits (e.g. 5551234567) for US numbers or include a +(country code) for international numbers (e.g. +442038078196)'
                        }));
                      }
                    }}
                    placeholder="e.g., 5551234567 or +442038078196"
                    required
                    disabled={ehrImported}
                  />
                </TextField.Root>
                {fieldErrors.phone_number ? (
                  <Text size="1" color="red" className="mt-1">{fieldErrors.phone_number}</Text>
                ) : (
                  <Text size="1" color="gray" className="mt-1">Enter 10 digits (e.g. 5551234567) or international format with + (e.g. +442038078196)</Text>
                )}
              </Box>

              <Box className="max-w-xs">
                <Text size="2" weight="medium" className="mb-2 block">Gender *</Text>
                <Flex gap="3">
                  {['male', 'female', 'other'].map((genderOption) => (
                    <label key={genderOption} className={`flex items-center gap-2 ${ehrImported ? '' : 'cursor-pointer'}`}>
                      <input
                        type="radio"
                        name="gender"
                        value={genderOption}
                        checked={patientData.gender === genderOption}
                        onChange={(e) => {
                          setPatientData(prev => ({ ...prev, gender: e.target.value }));
                          if (fieldErrors.gender) {
                            setFieldErrors(prev => ({ ...prev, gender: undefined }));
                          }
                        }}
                        className="w-4 h-4"
                        style={fieldErrors.gender ? { accentColor: 'red' } : {}}
                        disabled={ehrImported}
                      />
                      <Text size="2">{genderOption.charAt(0).toUpperCase() + genderOption.slice(1)}</Text>
                    </label>
                  ))}
                </Flex>
                {fieldErrors.gender && (
                  <Text size="1" color="red" className="mt-1">{fieldErrors.gender}</Text>
                )}
              </Box>

              {ehrImported && ehrImportResult && (
                <Callout.Root color="green" className="mt-4">
                  <Callout.Icon><CheckCircledIcon /></Callout.Icon>
                  <Callout.Text>
                    Patient data imported from EHR.
                    {ehrImportResult.created ? ' New patient created.' : ' Existing patient updated.'}
                    {' '}{ehrImportResult.problems_count} problems,
                    {' '}{ehrImportResult.medications_count} medications,
                    {' '}{ehrImportResult.vitals_count} vitals imported.
                  </Callout.Text>
                </Callout.Root>
              )}
              {ehrImported && (
                <Flex justify="end" className="mt-2">
                  <Button variant="soft" color="red" onClick={handleRejectEhrImport} disabled={loading}>
                    <CrossCircledIcon />
                    Reject Imported Information
                  </Button>
                </Flex>
              )}
              {voiceEnabled && (
                <Box className="max-w-xs">
                  <Text size="2" weight="medium" className="mb-2 block">Survey Contact Method</Text>
                  <Flex gap="3">
                    {[
                      { value: 'sms', label: 'SMS (text message link)' },
                      { value: 'voice', label: 'Voice AI (automated phone call)' },
                    ].map((option) => (
                      <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="preferred_contact_method"
                          value={option.value}
                          checked={patientData.preferred_contact_method === option.value}
                          onChange={(e) => {
                            setPatientData(prev => ({ ...prev, preferred_contact_method: e.target.value as 'sms' | 'voice' }));
                          }}
                          className="w-4 h-4"
                        />
                        <Text size="2">{option.label}</Text>
                      </label>
                    ))}
                  </Flex>
                </Box>
              )}
              {supportedLanguages.length > 1 && (
                <Box className="max-w-xs">
                  <Text size="2" weight="medium" className="mb-2 block">Preferred Language</Text>
                  <select
                    value={patientData.language}
                    onChange={(e) => setPatientData(prev => ({ ...prev, language: e.target.value }))}
                    style={{ padding: '8px', borderRadius: '6px', border: '1px solid var(--gray-7)', fontSize: '14px', width: '100%' }}
                  >
                    {supportedLanguages.map(lang => (
                      <option key={lang.code} value={lang.code}>
                        {lang.name} ({lang.native_name})
                      </option>
                    ))}
                  </select>
                </Box>
              )}
            </Flex>
          </Card>
        )}

        {/* Step 2: Document Upload - Loading State */}
        {activeStep === 1 && loading && (
          <Card className="p-6">
            <Box className="flex flex-col items-center justify-center py-8">
              <Heading size="5" className="text-slate-800 mb-2">
                Processing Documents
              </Heading>
              <Text size="3" className="text-slate-600 text-center mb-6">
                Uploading and parsing your documents...
              </Text>

              {/* Overall Progress (time-based; the per-file badges below remain truthful) */}
              {uploadProgress.files.length > 0 && (
                <Box className="w-full max-w-2xl">
                  <TimedProgress
                    active={loading}
                    estimatedDurationMs={Math.max(1, uploadProgress.files.length) * DOC_PARSE_MS_PER_FILE}
                    steps={DOC_PARSE_PROGRESS_STEPS}
                    className="mb-6"
                  />

                  {/* Current File */}
                  {uploadProgress.currentFile && (
                    <Box className="bg-blue-50 p-4 rounded-lg mb-6">
                      <Text size="2" weight="medium" className="text-blue-700">
                        Currently processing: {uploadProgress.currentFile}
                      </Text>
                    </Box>
                  )}

                  {/* File List */}
                  <Box className="max-h-80 overflow-y-auto space-y-3">
                    {uploadProgress.files.map((file, index) => (
                      <Box key={index} className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                        <Flex align="center" justify="between" className="mb-2">
                          <Text size="2" className="font-medium truncate flex-1 mr-3">
                            {file.name}
                          </Text>
                          <Box>
                            {file.status === 'completed' && (
                              <Badge color="green" variant="soft">
                                <CheckIcon width="12" height="12" className="mr-1" />
                                Completed
                              </Badge>
                            )}
                            {file.status === 'error' && (
                              <Badge color="red" variant="soft">
                                <CrossCircledIcon width="12" height="12" className="mr-1" />
                                Failed
                              </Badge>
                            )}
                            {file.status === 'uploading' && (
                              <Badge color="blue" variant="soft">
                                Uploading...
                              </Badge>
                            )}
                            {file.status === 'pending' && (
                              <Badge color="gray" variant="soft">
                                Waiting
                              </Badge>
                            )}
                          </Box>
                        </Flex>
                        {/* Per-file progress is conveyed by the overall TimedProgress bar and
                            the status badge above; an indeterminate stripe avoids a fake %. */}
                        {file.status === 'uploading' && (
                          <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
                            <div className="bg-blue-600 h-1.5 rounded-full w-1/3 animate-pulse" />
                          </div>
                        )}
                        {file.error && (
                          <Text size="1" color="red" className="mt-2">
                            {file.error}
                          </Text>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              <Text size="2" className="text-slate-500 mt-6">
                This may take a few moments...
              </Text>
            </Box>
          </Card>
        )}

        {/* Step 2: Document Upload Form */}
        {activeStep === 1 && !loading && (
          <Card className="p-6">
            <Heading size="5" className="text-slate-800 mb-4">
              Document Upload & Information
            </Heading>

            {uploadStepPrompt === 'already-processed' && (
              <Callout.Root color="blue" className="mb-4">
                <Callout.Text>
                  Documents were already uploaded and processed. You can proceed to review, or upload new documents to reprocess.
                </Callout.Text>
              </Callout.Root>
            )}
            {/* PHA-1072 (second report): the clinician chose "Upload new problem list and
                medications", so telling them the documents are already done is the opposite of
                what they asked for — and continuing from here uploads nothing. */}
            {uploadStepPrompt === 'awaiting-new-documents' && (
              <Callout.Root color="amber" className="mb-4">
                <Callout.Text>
                  No new documents have been added yet. Upload the new problem list and/or
                  medication list below — whichever you have, both are not required. If you
                  continue without uploading, nothing is re-read and the care plan is rebuilt
                  from the lists already on the chart.
                </Callout.Text>
              </Callout.Root>
            )}

            {/* Vital Signs Section */}
            <VitalSignsInput
              vitalSigns={vitalSigns}
              onChange={setVitalSigns}
            />

            <Separator className="my-6" />

            {/* Problem List Section */}
            <Box className="mb-6">
              <SectionHeading>
                Problem List
              </SectionHeading>
              {documentUploadErrors.problemList && (
                <Text size="2" color="red" className="mb-2 block">
                  {documentUploadErrors.problemList}
                </Text>
              )}
              <Box
                className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center mb-4 transition-colors"
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.add('border-green-400', 'bg-green-50');
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.remove('border-green-400', 'bg-green-50');
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.remove('border-green-400', 'bg-green-50');
                  const files = Array.from(e.dataTransfer.files);
                  const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
                  const validFiles = files.filter(file => {
                    const fileName = file.name.toLowerCase();
                    return allowedExtensions.some(ext => fileName.endsWith(ext));
                  });
                  if (validFiles.length > 0) {
                    const event = { target: { files: validFiles } } as any;
                    handleProblemListUpload(event);
                    setDocumentUploadErrors(prev => ({ ...prev, problemList: undefined }));
                  }
                  if (validFiles.length !== files.length) {
                    toast.error(`Only PDF, DOC, DOCX, TXT, JPG, PNG, GIF, BMP, WEBP files are accepted. ${files.length - validFiles.length} file(s) rejected.`);
                  }
                }}
              >
                <Button
                  variant="solid"
                  color="green"
                  size="3"
                  className="px-6 py-3"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.multiple = true;
                    input.accept = '.pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.bmp,.webp';
                    input.onchange = (event) => {
                      handleProblemListUpload(event as any);
                      setDocumentUploadErrors(prev => ({ ...prev, problemList: undefined }));
                    };
                    input.click();
                  }}
                >
                  <UploadIcon className="w-5 h-5 mr-2" />
                  Select Documents
                </Button>
                <Text size="2" className="text-slate-400 mt-3 block">or drag and drop files here</Text>
                <Text size="2" className="text-slate-500 mt-2 block">Accepts: PDF, DOC, DOCX, TXT, JPG, PNG, GIF, BMP, WEBP</Text>
              </Box>

              {/* Problem List Files */}
              {problemListFiles.length > 0 && (
                <Box className="mb-4">
                  <Text size="2" className="text-slate-600 mb-2 block">Selected files:</Text>
                  <Flex direction="column" gap="2">
                    {problemListFiles.map((file, index) => (
                      <Badge key={index} variant="soft" className="flex items-center justify-between p-2">
                        <Text size="2">{file.name} ({Math.round(file.size / 1024)}KB)</Text>
                        <IconButton
                          size="1"
                          variant="ghost"
                          onClick={() => removeProblemFile(index)}
                        >
                          <Cross2Icon width="12" height="12" />
                        </IconButton>
                      </Badge>
                    ))}
                  </Flex>
                </Box>
              )}

              {/* Previously uploaded file names (continuation flow) */}
              {problemListFiles.length === 0 && documentsProcessed && previousDocumentNames.problem_list && previousDocumentNames.problem_list.length > 0 && (
                <Box className="mb-4">
                  <Text size="2" className="text-blue-600 mb-2 block">Previously uploaded file names:</Text>
                  <Flex direction="column" gap="2">
                    {previousDocumentNames.problem_list.map((name: string, index: number) => (
                      <Badge key={index} variant="soft" color="blue" className="p-2">
                        <Text size="2">{name}</Text>
                      </Badge>
                    ))}
                  </Flex>
                </Box>
              )}

              <Text size="2" className="text-slate-600 mb-2 block">
                Or enter problem list as text (or paste images with Ctrl+V / Cmd+V):
              </Text>
              <TextArea
                placeholder="Enter patient problems, diagnoses, and conditions... You can also paste images directly here (Ctrl+V / Cmd+V)"
                value={problemListText}
                onChange={(e) => {
                  setProblemListText((e.target as HTMLTextAreaElement).value);
                  if (documentUploadErrors.problemList) {
                    setDocumentUploadErrors(prev => ({ ...prev, problemList: undefined }));
                  }
                  if (parseErrors.problemList) {
                    setParseErrors(prev => ({ ...prev, problemList: undefined }));
                  }
                }}
                onPaste={(e) => handlePaste(e, 'problem')}
                className="w-full min-h-24 p-3 border border-slate-300 rounded-lg resize-y"
              />
              {parseErrors.problemList && (
                <Box className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <Text size="2" color="red" weight="medium">
                    Failed to extract problems: {parseErrors.problemList}
                  </Text>
                </Box>
              )}
              {pastedProblemImages.length > 0 && (
                <Box className="mt-4">
                  <Text size="2" className="text-slate-600 mb-2 block">Pasted images:</Text>
                  <Flex direction="column" gap="2">
                    {pastedProblemImages.map((file, index) => (
                      <Badge key={index} variant="soft" color="green" className="flex items-center justify-between p-2">
                        <Text size="2">📋 {file.name} ({Math.round(file.size / 1024)}KB)</Text>
                        <IconButton
                          size="1"
                          variant="ghost"
                          onClick={() => removePastedProblemImage(index)}
                        >
                          <Cross2Icon width="12" height="12" />
                        </IconButton>
                      </Badge>
                    ))}
                  </Flex>
                </Box>
              )}
            </Box>

            {/* Medication List Section */}
            <Box className="mb-6">
              <SectionHeading>
                Medication List (optional)
              </SectionHeading>
              {documentUploadErrors.medicationList && (
                <Text size="2" color="red" className="mb-2 block">
                  {documentUploadErrors.medicationList}
                </Text>
              )}
              <Box
                className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center mb-4 transition-colors"
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.add('border-green-400', 'bg-green-50');
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.remove('border-green-400', 'bg-green-50');
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.remove('border-green-400', 'bg-green-50');
                  const files = Array.from(e.dataTransfer.files);
                  const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
                  const validFiles = files.filter(file => {
                    const fileName = file.name.toLowerCase();
                    return allowedExtensions.some(ext => fileName.endsWith(ext));
                  });
                  if (validFiles.length > 0) {
                    const event = { target: { files: validFiles } } as any;
                    handleMedicationListUpload(event);
                    setDocumentUploadErrors(prev => ({ ...prev, medicationList: undefined }));
                  }
                  if (validFiles.length !== files.length) {
                    toast.error(`Only PDF, DOC, DOCX, TXT, JPG, PNG, GIF, BMP, WEBP files are accepted. ${files.length - validFiles.length} file(s) rejected.`);
                  }
                }}
              >
                <Button
                  variant="solid"
                  color="green"
                  size="3"
                  className="px-6 py-3"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.multiple = true;
                    input.accept = '.pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.bmp,.webp';
                    input.onchange = (event) => {
                      handleMedicationListUpload(event as any);
                      setDocumentUploadErrors(prev => ({ ...prev, medicationList: undefined }));
                    };
                    input.click();
                  }}
                >
                  <UploadIcon className="w-5 h-5 mr-2" />
                  Select Documents
                </Button>
                <Text size="2" className="text-slate-400 mt-3 block">or drag and drop files here</Text>
                <Text size="2" className="text-slate-500 mt-2 block">Accepts: PDF, DOC, DOCX, TXT, JPG, PNG, GIF, BMP, WEBP</Text>
              </Box>

              {/* Medication List Files */}
              {medicationListFiles.length > 0 && (
                <Box className="mb-4">
                  <Text size="2" className="text-slate-600 mb-2 block">Selected files:</Text>
                  <Flex direction="column" gap="2">
                    {medicationListFiles.map((file, index) => (
                      <Badge key={index} variant="soft" className="flex items-center justify-between p-2">
                        <Text size="2">{file.name} ({Math.round(file.size / 1024)}KB)</Text>
                        <IconButton
                          size="1"
                          variant="ghost"
                          onClick={() => removeMedicationFile(index)}
                        >
                          <Cross2Icon width="12" height="12" />
                        </IconButton>
                      </Badge>
                    ))}
                  </Flex>
                </Box>
              )}

              {/* Previously uploaded file names (continuation flow) */}
              {medicationListFiles.length === 0 && documentsProcessed && previousDocumentNames.medication_list && previousDocumentNames.medication_list.length > 0 && (
                <Box className="mb-4">
                  <Text size="2" className="text-blue-600 mb-2 block">Previously uploaded file names:</Text>
                  <Flex direction="column" gap="2">
                    {previousDocumentNames.medication_list.map((name: string, index: number) => (
                      <Badge key={index} variant="soft" color="blue" className="p-2">
                        <Text size="2">{name}</Text>
                      </Badge>
                    ))}
                  </Flex>
                </Box>
              )}

              <Text size="2" className="text-slate-600 mb-2 block">
                Or enter medication list as text (or paste images with Ctrl+V / Cmd+V):
              </Text>
              <TextArea
                placeholder="Enter current medications, dosages, and frequencies... You can also paste images directly here (Ctrl+V / Cmd+V)"
                value={medicationListText}
                onChange={(e) => {
                  setMedicationListText((e.target as HTMLTextAreaElement).value);
                  if (documentUploadErrors.medicationList) {
                    setDocumentUploadErrors(prev => ({ ...prev, medicationList: undefined }));
                  }
                  if (parseErrors.medicationList) {
                    setParseErrors(prev => ({ ...prev, medicationList: undefined }));
                  }
                }}
                onPaste={(e) => handlePaste(e, 'medication')}
                className="w-full min-h-24 p-3 border border-slate-300 rounded-lg resize-y"
              />
              {parseErrors.medicationList && (
                <Box className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <Text size="2" color="red" weight="medium">
                    Failed to extract medications: {parseErrors.medicationList}
                  </Text>
                </Box>
              )}
              {pastedMedicationImages.length > 0 && (
                <Box className="mt-4">
                  <Text size="2" className="text-slate-600 mb-2 block">Pasted images:</Text>
                  <Flex direction="column" gap="2">
                    {pastedMedicationImages.map((file, index) => (
                      <Badge key={index} variant="soft" color="green" className="flex items-center justify-between p-2">
                        <Text size="2">📋 {file.name} ({Math.round(file.size / 1024)}KB)</Text>
                        <IconButton
                          size="1"
                          variant="ghost"
                          onClick={() => removePastedMedicationImage(index)}
                        >
                          <Cross2Icon width="12" height="12" />
                        </IconButton>
                      </Badge>
                    ))}
                  </Flex>
                </Box>
              )}
            </Box>
          </Card>
        )}

        {/* Step 3: Review Patient Information */}
        {activeStep === 2 && (
          <>
            <ApprovalWorkflow
              problems={extractedData.problems as ProblemApprovalItem[]}
              medications={extractedData.medications as MedicationApprovalItem[]}
              onProblemsChange={(problems) => {
                setExtractedData(prev => ({ ...prev, problems: problems as ParsedProblem[] }));
                if (carePlanGenerated) {
                  setCarePlanDirtySinceGeneration(true);
                  persistCarePlanDirty(true);
                }
              }}
              onMedicationsChange={(medications) => {
                setExtractedData(prev => ({ ...prev, medications: medications as ParsedMedication[] }));
                if (carePlanGenerated) {
                  setCarePlanDirtySinceGeneration(true);
                  persistCarePlanDirty(true);
                }
              }}
              onApprove={approveExtractedData}
              loading={loading}
            />
          </>
        )}

        {/* Step 4: Approve Care Plan */}
        {activeStep === 3 && (
          <CarePlanApproval
            carePlanData={carePlanData}
            patientId={patientData.patient_id}
            isOnboarding
            onUnsavedChangesChange={onUnsavedChangesChange}
            registerDraftControls={registerDraftControls}
            onApprove={handleCarePlanApproval}
            onSaveDraftAndExit={handleSaveDraftAndExit}
            onCancelRegeneration={refreshSource ? handleCancelRegeneration : undefined}
            refreshPreselectDiseases={refreshSource ? refreshBaseline?.currentPlanDiseases : undefined}
            refreshHasApprovedPlan={refreshSource ? refreshBaseline?.hasApprovedPlan : undefined}
            isCarePlanRefresh={!!refreshSource}
            // PHA-1162: a VoP goal saved from the approval step — re-read the patient's
            // person-centered data so the section, the approve check and the draft PDF see it.
            onVoiceOfPatientSaved={refreshVoiceOfPatientForPdf}
            onDeclineConsent={handleConsentDeclined}
            initialConsentStatus={loadedConsentStatus}
            loading={loading}
            onGenerateCarePlan={generateCarePlan}
            carePlanLoading={carePlanLoading}
            carePlanGenerated={carePlanGenerated}
            approvedProblems={approvedProblemsForCarePlan}
            reviewChangedSinceGeneration={carePlanDirtySinceGeneration}
            onRegenerateCarePlan={generateCarePlan}
            noTargetDatesEnabled={noTargetDatesEnabled}
            carePlanReviewMonths={carePlanReviewMonths}
            patientForPdf={{
              first_name: patientData.first_name,
              last_name: patientData.last_name,
              date_of_birth: patientData.date_of_birth,
              patient_id: patientData.patient_id,
              // PHA-737: program/logo for the draft PDF (fetched on Step-3 entry).
              apcm_tier: patientPdfMeta.apcm_tier,
              has_rpm_device: patientPdfMeta.has_rpm_device,
              language: patientPdfMeta.language,
              logo_path: patientPdfMeta.logo_path,
              organization_name: patientPdfMeta.organization_name,
              // PHA-865: full problem/medication lists + PCMH gate for the draft PDF.
              // Exclude items the clinician has explicitly rejected (approved === false) so the
              // draft preview matches what will actually be saved on approval.
              voice_of_patient_enabled: voiceOfPatientEnabled,
              // PHA-895 follow-up (PHA-737 parity): approved-only onboarding items (PHA-865),
              // enriched with the onset/start/end dates the chart shows, so the draft PDF's Full
              // Problem/Med List matches the patient chart without re-introducing rejected items.
              problems: problemsForPdf,
              medications: medicationsForPdf,
              // PHA-899: carry person-centered (Voice of the Patient) data into the draft PDF so
              // its "Voice of the patient" section matches the patient chart's Download Care Plan.
              // Previously omitted, so the onboarding draft always showed "No goals were defined."
              person_centered_goals: patientPdfMeta.person_centered_goals,
              pcm_barriers: patientPdfMeta.pcm_barriers,
              gas_definitions: patientPdfMeta.gas_definitions,
            }}
          />
        )}

        {/* Step 5: Complete */}
        {activeStep === 4 && !surveyResultsGenerated && (
          <Card className="p-6 text-center">
            <Box className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-6"></div>
              <Heading size="5" className="text-slate-800 mb-2">
                Generating Survey Questions
              </Heading>
              <Text size="3" className="text-slate-600">
                Please wait while we generate personalized survey questions based on the care plan...
              </Text>
            </Box>
          </Card>
        )}

        {activeStep === 4 && surveyResultsGenerated && (
          <SurveyResultsDisplay
            surveys={surveyResults}
            onComplete={() => {
              resetWorkflow();
            }}
          />
        )}

        {/* Navigation Buttons */}
        <Flex justify="between" className="mt-6">
          <Button
            onClick={handleBack}
            disabled={activeStep === 0 || loading || activeStep === 4}
            variant="outline"
            style={{ visibility: activeStep === 4 ? 'hidden' : 'visible' }}
          >
            <ArrowLeftIcon width="16" height="16" className="mr-2" />
            Previous
          </Button>

          {activeStep < steps.length - 1 && activeStep !== 2 && activeStep !== 3 && (
            <Button
              variant="solid"
              onClick={handleNext}
              disabled={loading || (activeStep === 1 && !canProcessDocuments)}
            >
              {activeStep === 0 ? (ehrImported ? 'Review & Generate Care Plan' : 'Next: Document Upload') :
                activeStep === 1 ? (documentsProcessed && processedInputSnapshot === getInputSnapshot()
                  ? getUploadStepContinueLabel(uploadStepPrompt)
                  : 'Process Documents') :
                  'Next'}
              {loading ? (
                <div className="ml-2 animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <ArrowRightIcon width="16" height="16" className="ml-2" />
              )}
            </Button>
          )}

        </Flex>
      </Card>

      {/* Duplicate Patient Confirmation Dialog */}
      <Dialog.Root open={showDuplicateDialog} onOpenChange={(open) => !open && handleDuplicateDialogClose(false)}>
        <Dialog.Content style={{ maxWidth: 450 }}>
          <Dialog.Title>Patient Already Exists</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            {duplicateDialogMessage}
          </Dialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray" onClick={() => handleDuplicateDialogClose(false)}>
                No
              </Button>
            </Dialog.Close>
            <Button variant="solid" onClick={() => handleDuplicateDialogClose(true)}>
              Yes, Update
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </Box>
  );
};

export default OnboardingWorkflow;
